import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { Incident, Installment, OrderDetail, OrderLine, OrderSummary, Payable, PendingLine, PurchaseCatalogs, PurchaseNeed, Supplier, SupplierDetail } from "../../../../services/v2/purchases.service";
import ReasonPrompt from "../../../../components/kits/reasonPrompt";
import ProductPicker from "../products/productPicker";
import ComprarPorTallas, { PickedPiece } from "./comprarPorTallas";
import { Empty, Loading, Pager, apiError, openBlob, ui, useCampus, useDebounced, useIdleLocked, usePerms } from "../kits/shared";

/*
	Tienda → Compras (2026-09-30). Una pantalla, tres permisos:
	  purchaseOrders    ver, crear y enviar a Contraloría, editar borrador, cancelar borrador
	  purchaseApprovals Contraloría: aprobar con pago, regresar, pagos, costos, cerrar
	  purchaseReceipts  bodega: recibir contra la orden, con fotos
	Sin <label>: el CSS global los posiciona absolutos.
*/

const S = { DRAFT: 1, PENDING: 2, IN_TRANSIT: 3, PARTLY: 4, RECEIVED: 5, CLOSED: 6, CANCELLED: 7 };

const TABS: { key: string; label: string; statusIds?: number[] }[] = [
	{ key: "pending", label: "Por aprobar", statusIds: [S.PENDING] },
	{ key: "transit", label: "En camino", statusIds: [S.IN_TRANSIT, S.PARTLY] },
	{ key: "receive", label: "Por recibir" },
	{ key: "payables", label: "Por pagar" },
	{ key: "drafts", label: "Borradores", statusIds: [S.DRAFT] },
	{ key: "done", label: "Recibidas", statusIds: [S.RECEIVED, S.CLOSED] },
	{ key: "all", label: "Todas" },
	{ key: "incidents", label: "Incidencias" },
	{ key: "needs", label: "Qué comprar" },
	{ key: "suppliers", label: "Proveedores" }
];

const statusColor = (statusId: number): { color: string; background: string } =>
	statusId === S.PENDING ? { color: "var(--wa-acento-texto)", background: "var(--wa-acento-suave)" }
		: statusId === S.IN_TRANSIT || statusId === S.PARTLY ? { color: "var(--wa-tinta)", background: "var(--wa-papel-3)" }
		: statusId === S.RECEIVED ? { color: "var(--wa-verde)", background: "var(--wa-verde-suave)" }
		: statusId === S.CANCELLED ? { color: "var(--wa-apagado)", background: "var(--wa-papel-2)" }
		: { color: "var(--wa-apagado)", background: "var(--wa-papel-2)" };

const Chip: React.FC<{ statusId: number; text: string }> = ({ statusId, text }) =>
	<span style={{ ...statusColor(statusId), borderRadius: "10px", padding: "1px 9px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>{text}</span>;

const fmt = (symbol: string, value: number | null | undefined): string => value === null || value === undefined ? "-" : symbol + value.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dmy = (iso: string | null): string => iso ? iso.split("-").reverse().join("/") : "-";
const errorOf = (requestError: any, fallback: string): string => {
	const payload = requestError?.response?.data;
	const first = payload?.errors ? Object.values(payload.errors)[0] : null;
	return first ? String((first as any)[0]) : (payload?.message || fallback);
};

/** Pieces are whole numbers: "" counts as 0, "2.5" or "-1" are not valid. */
const isWhole = (value: string | undefined): boolean => value === undefined || value.trim() === "" || /^\d+$/.test(value.trim());

/** Laptop with mouse (and USB reader) vs phone: on touch screens focusing a field opens the keyboard over the content. */
const finePointer = (): boolean => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: fine)").matches;

/** True under `px` wide (phones): tables become one card per row. */
const useNarrow = (px = 600): boolean => {
	const query = "(max-width: " + (px - 1) + "px)";
	const [narrow, setNarrow] = useState<boolean>(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches);
	useEffect(() => {
		if (!window.matchMedia) return;
		const media = window.matchMedia(query);
		const onChange = () => setNarrow(media.matches);
		onChange();
		if (media.addEventListener) media.addEventListener("change", onChange); else media.addListener(onChange);
		return () => { if (media.removeEventListener) media.removeEventListener("change", onChange); else media.removeListener(onChange); };
	}, [query]);
	return narrow;
};

/** Lists what is missing under a disabled button (at most 3 items, then "y N más"). */
const Missing: React.FC<{ items: string[] }> = ({ items }) => items.length < 1 ? null
	: <div style={{ ...ui.small, color: "var(--wa-oro)", textAlign: "right", marginTop: "6px" }}>{items.slice(0, 3).join(" · ")}{items.length > 3 ? " · y " + (items.length - 3) + " más" : ""}</div>;

const Modal: React.FC<{ width?: string; busy?: boolean; onClose: () => void; children: React.ReactNode }> = ({ width, busy, onClose, children }) => {
	const dialogRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		/*
			Only the dialog on top answers Escape (QA 2026-10-19: Escape on "Anular costo" also closed the order behind it).
			Capture phase, so this runs before ReasonPrompt's own listener and before React re-renders anything; the first
			modal that acts marks the event so the one underneath does not act on the same key press.
		*/
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== "Escape" || busy || (event as any).waModalHandled) return;
			const dialogs = document.querySelectorAll("[role=\"dialog\"][aria-modal=\"true\"]");
			if (dialogs.length > 0 && dialogs[dialogs.length - 1] !== dialogRef.current) return;
			(event as any).waModalHandled = true;
			onClose();
		};
		window.addEventListener("keydown", onKey, true);
		return () => window.removeEventListener("keydown", onKey, true);
	}, [busy, onClose]);
	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
		<div ref={dialogRef} className="card" style={{ width: "100%", maxWidth: width || "720px", maxHeight: "94vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(43,28,22,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">{children}</div>
		</div>
	</div>;
};

const Compras: React.FC = () => {
	const { campusId } = useCampus();
	const idleLocked = useIdleLocked();
	const orderPerms: any = usePerms("purchaseOrders");
	const approvalPerms: any = usePerms("purchaseApprovals");
	const receiptPerms: any = usePerms("purchaseReceipts");
	const http = HttpManager.getInstance();

	const [catalogs, setCatalogs] = useState<PurchaseCatalogs | null>(null);
	const [tabKey, setTabKey] = useState<string>("transit");
	const [counts, setCounts] = useState<Record<string, number>>({});
	const [query, setQuery] = useState<string>("");
	const debouncedQuery = useDebounced(query);
	const [rows, setRows] = useState<OrderSummary[]>([]);
	const [needs, setNeeds] = useState<PurchaseNeed[]>([]);
	const [pendingLines, setPendingLines] = useState<PendingLine[]>([]);
	const [payables, setPayables] = useState<Payable[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [page, setPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [openId, setOpenId] = useState<number | null>(null);
	const [form, setForm] = useState<{ order: OrderDetail | null; prefill?: { articleId: number; name: string; waCode: string | null; quantity: number }[] } | null>(null);
	const [supplierForm, setSupplierForm] = useState<Supplier | null | undefined>(undefined);
	const [supplierDetailId, setSupplierDetailId] = useState<number | null>(null);
	const [paying, setPaying] = useState<{ supplierId: number | null; orderId: number | null } | null>(null);
	const [newSupplierId, setNewSupplierId] = useState<number | null>(null);

	const loadCatalogs = useCallback(() => {
		if (!campusId) return;
		http.purchaseService.catalogs(campusId).then((response) => setCatalogs(response.data)).catch(() => undefined);
	}, [campusId]);

	const loadCounts = useCallback(() => {
		if (!campusId) return;
		http.purchaseService.counts(campusId).then((response) => {
			const byStatus = response.data;
			const n = (ids: number[]) => ids.reduce((sum, id) => sum + (byStatus[id] || 0), 0);
			setCounts({ pending: n([S.PENDING]), transit: n([S.IN_TRANSIT, S.PARTLY]), drafts: n([S.DRAFT]) });
		}).catch(() => undefined);
	}, [campusId]);

	useEffect(() => { loadCatalogs(); loadCounts(); }, [loadCatalogs, loadCounts]);

	const load = useCallback(async () => {
		if (!campusId || tabKey === "suppliers" || tabKey === "incidents") return;
		setLoading(true);
		setError(null);
		try {
			if (tabKey === "needs") {
				const response = await http.purchaseService.needs(campusId);
				setNeeds(response.data || []);
			} else if (tabKey === "receive") {
				const response = await http.purchaseService.pendingLines(campusId);
				setPendingLines(response.data || []);
			} else if (tabKey === "payables") {
				const response = await http.purchaseService.payables(campusId);
				setPayables(response.data || []);
			} else {
				const tab = TABS.find((entry) => entry.key === tabKey);
				const response = await http.purchaseService.list(campusId, { q: debouncedQuery.trim() || undefined, statusIds: tab?.statusIds?.join(",") }, page, 100);
				setRows(response.data.data || []);
				setTotal(response.data.total || 0);
				setLastPage(response.data.last_page || 1);
			}
		} catch (requestError: any) {
			setError(errorOf(requestError, "No se pudieron cargar las compras."));
		} finally {
			setLoading(false);
		}
	}, [campusId, tabKey, debouncedQuery, page]);

	useEffect(() => { load(); }, [load]);

	const refresh = (message?: string) => {
		if (message) setNotice(message);
		load();
		loadCounts();
	};

	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;

	// Whoever only receives gets the amounts as null from the server: no money columns at all instead of dashes.
	const listMoney = rows.some((row) => (row.subtotal as number | null) !== null);

	return <div style={ui.page}>
		<div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
			<h2 style={ui.title}>Compras</h2>
			{orderPerms.create ? <button type="button" className="btn btn-primary rounded-pill" disabled={idleLocked || !catalogs} onClick={() => setForm({ order: null })}>Nueva Orden</button> : null}
		</div>
		<div style={ui.tabs}>
			{TABS.filter((tab) => (tab.key !== "payables" || approvalPerms.read) && (!["needs", "drafts"].includes(tab.key) || orderPerms.create || approvalPerms.read)).map((tab) => <button key={tab.key} type="button" disabled={idleLocked}
				className={"btn rounded-pill " + (tab.key === tabKey ? "btn-primary" : "btn-outline-secondary")}
				onClick={() => { setTabKey(tab.key); setPage(1); setRows([]); }}>
				{tab.label}{counts[tab.key] ? <span style={ui.badge(tab.key === tabKey)}>{counts[tab.key]}</span> : null}
			</button>)}
		</div>
		{notice ? <div className="alert alert-success" style={{ padding: "8px 12px", fontSize: "14px", display: "flex", justifyContent: "space-between" }}>
			<span>{notice}</span><button type="button" className="btn-close" onClick={() => setNotice(null)} />
		</div> : null}

		{tabKey === "suppliers" ? <SuppliersTab campusId={campusId} catalogs={catalogs} canCreate={!!orderPerms.create} canEdit={!!orderPerms.update} onEdit={(supplier) => setSupplierForm(supplier)}
			onOpen={(supplierId) => setSupplierDetailId(supplierId)} reloadKey={supplierForm === undefined ? 1 : 0} />
			: tabKey === "incidents" ? <IncidentsTab campusId={campusId} canResolve={!!approvalPerms.create} onOpen={setOpenId} />
			: tabKey === "receive" ? <PendingLinesTab rows={pendingLines} loading={loading} error={error} onOpen={setOpenId} />
			: tabKey === "payables" ? <PayablesTab rows={payables} loading={loading} error={error} onOpen={setOpenId}
				onPay={approvalPerms.create ? (supplierId) => setPaying({ supplierId, orderId: null }) : undefined} />
			: tabKey === "needs" ? <NeedsTab needs={needs} loading={loading} error={error} canCreate={!!orderPerms.create}
				onCreate={(items) => setForm({ order: null, prefill: items })} />
			: <>
				<div style={{ ...ui.card, ...ui.filters }}>
					<div style={ui.field("300px")}>
						<span style={ui.caption}>Buscar</span>
						<input type="text" className="form-control" style={ui.input} placeholder="Folio, proveedor, factura o producto"
							value={query} disabled={idleLocked} onChange={(event) => { setQuery(event.target.value); setPage(1); }} />
					</div>
				</div>
				<Pager page={page} lastPage={lastPage} total={total} unit="órdenes" busy={loading} onPage={setPage} />
				<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
					{loading ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
						: rows.length < 1 ? <Empty text="Sin órdenes." />
						: <table style={{ width: "100%", borderCollapse: "collapse" }}>
							<thead><tr>
								<th style={ui.th}>Orden</th>
								<th style={ui.th}>Proveedor</th>
								<th style={ui.th}>Llega</th>
								<th style={ui.th}>Bodega</th>
								<th style={{ ...ui.th, ...ui.right }}>Piezas</th>
								{listMoney ? <th style={{ ...ui.th, ...ui.right }}>Total</th> : null}
								{listMoney ? <th style={{ ...ui.th, ...ui.right }}>Pagado</th> : null}
								<th style={ui.th}>Estado</th>
							</tr></thead>
							<tbody>{rows.map((row) => <tr key={row.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(row.id)}>
								<td style={ui.td}>
									<div style={{ fontWeight: 700, fontFamily: "monospace" }}>{row.folio}</div>
									<div style={ui.tiny}>{row.createdAt} · {row.createdBy}</div>
								</td>
								<td style={ui.td}>
									<div style={{ fontWeight: 600 }}>{row.supplierName}</div>
									{row.supplierReference ? <div style={ui.tiny}>{row.supplierReference}</div> : null}
								</td>
								<td style={{ ...ui.td, whiteSpace: "nowrap", color: row.late ? "var(--wa-oro)" : undefined, fontWeight: row.late ? 600 : 400 }}>
									{dmy(row.expectedAt)}{row.late ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>Atrasada</div> : null}
								</td>
								<td style={ui.td}>{row.warehouseName}</td>
								<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>
									{row.units}
									{row.received > 0 ? <div style={ui.tiny}>{row.received} recibidas{row.pending > 0 ? " · faltan " + row.pending : ""}</div> : null}
								</td>
								{listMoney ? <td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>
									{fmt(row.currencySymbol, row.subtotal)} <span style={ui.tiny}>{row.currencyCode}</span>
								</td> : null}
								{listMoney ? <td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>
									{row.paid > 0 ? fmt(row.currencySymbol, row.paid) : "-"}
									{row.paid > 0 && row.balance > 0 ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>saldo {fmt(row.currencySymbol, row.balance)}</div> : null}
								</td> : null}
								<td style={ui.td}>
									<Chip statusId={row.statusId} text={row.status} />
									{row.openIncidents > 0 ? <div style={{ ...ui.tiny, color: "var(--wa-oro)", fontWeight: 600, marginTop: "3px" }}>{row.openIncidents} incidencia{row.openIncidents === 1 ? "" : "s"} abierta{row.openIncidents === 1 ? "" : "s"}</div> : null}
									{row.costsFlag ? <div style={{ ...ui.tiny, color: "var(--wa-oro)", fontWeight: row.costsFlag === "alert" ? 700 : 400, marginTop: "3px" }}>Faltan flete o impuestos · {row.costsPendingDays} días</div> : null}
								</td>
							</tr>)}</tbody>
						</table>}
				</div>
			</>}

		{openId !== null ? <OrderModal campusId={campusId} orderId={openId} catalogs={catalogs}
			perms={{ orders: orderPerms, approvals: approvalPerms, receipts: receiptPerms }}
			onEdit={(order) => { setOpenId(null); setForm({ order }); }}
			onPay={(order) => { setOpenId(null); setPaying({ supplierId: order.supplierId, orderId: order.id }); }}
			onChanged={(message) => refresh(message)}
			onClose={() => setOpenId(null)} /> : null}
		{form && catalogs ? <OrderForm campusId={campusId} catalogs={catalogs} order={form.order} prefill={form.prefill}
			onNewSupplier={() => setSupplierForm(null)} selectSupplierId={newSupplierId}
			onSaved={(order, message) => { setForm(null); refresh(message); setOpenId(order.id); }}
			onClose={() => setForm(null)} /> : null}
		{supplierForm !== undefined && catalogs ? <SupplierForm campusId={campusId} catalogs={catalogs} supplier={supplierForm}
			onSaved={(saved) => { setSupplierForm(undefined); loadCatalogs(); if (form && saved) setNewSupplierId(saved.id); }} onClose={() => setSupplierForm(undefined)} /> : null}
		{supplierDetailId !== null ? <SupplierDetailModal campusId={campusId} supplierId={supplierDetailId} canPay={!!approvalPerms.create} canEdit={!!orderPerms.update}
			onOpenOrder={(orderId) => { setSupplierDetailId(null); setOpenId(orderId); }}
			onPay={(supplierId) => setPaying({ supplierId, orderId: null })}
			onEdit={(supplier) => { setSupplierDetailId(null); setSupplierForm(supplier); }}
			onClose={() => setSupplierDetailId(null)} /> : null}
		{paying && catalogs ? <SupplierPaymentModal campusId={campusId} catalogs={catalogs} supplierId={paying.supplierId} orderId={paying.orderId}
			onDone={(message) => { setPaying(null); refresh(message); }} onCancel={() => setPaying(null)} /> : null}
	</div>;
};

// ------------------------------------------------------------------ nueva orden / editar borrador

interface FormLine { articleId: number; name: string; waCode: string | null; quantity: string; unitCost: string }

const OrderForm: React.FC<{
	campusId: number; catalogs: PurchaseCatalogs; order: OrderDetail | null;
	prefill?: { articleId: number; name: string; waCode: string | null; quantity: number }[];
	onNewSupplier: () => void; selectSupplierId?: number | null; onSaved: (order: OrderDetail, message: string) => void; onClose: () => void;
}> = ({ campusId, catalogs, order, prefill, onNewSupplier, selectSupplierId, onSaved, onClose }) => {
	const [supplierId, setSupplierId] = useState<string>(order ? String(order.supplierId) : "");
	const [warehouseId, setWarehouseId] = useState<string>(order ? String(order.warehouseId) : String(catalogs.warehouses.find((w) => w.kind === "central")?.id || catalogs.warehouses[0]?.id || ""));
	const [currencyId, setCurrencyId] = useState<string>(order ? String(order.currencyId) : String(catalogs.localCurrencyId));
	const [exchangeRate, setExchangeRate] = useState<string>(order && order.exchangeRate !== 1 ? String(order.exchangeRate) : "");
	const [expectedAt, setExpectedAt] = useState<string>(order?.expectedAt || "");
	const [reference, setReference] = useState<string>(order?.supplierReference || "");
	const [notes, setNotes] = useState<string>(order?.notes || "");
	const [lines, setLines] = useState<FormLine[]>(order
		? order.lines.map((line) => ({ articleId: line.articleId, name: line.name, waCode: line.waCode, quantity: String(line.quantity), unitCost: String(line.unitCost) }))
		: (prefill || []).map((item) => ({ articleId: item.articleId, name: item.name, waCode: item.waCode, quantity: String(item.quantity), unitCost: "" })));
	const [files, setFiles] = useState<File[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [sizingModel, setSizingModel] = useState<number | null>(null);

	// Proveedor recién dado de alta desde la orden: queda elegido.
	useEffect(() => { if (selectSupplierId) setSupplierId(String(selectSupplierId)); }, [selectSupplierId, catalogs.suppliers.length]);

	// El proveedor trae su moneda habitual.
	useEffect(() => {
		if (order) return;
		const supplier = catalogs.suppliers.find((entry) => String(entry.id) === supplierId);
		if (supplier?.currencyId) setCurrencyId(String(supplier.currencyId));
	}, [supplierId]);

	const foreign = Number(currencyId) !== catalogs.localCurrencyId;
	const currency = catalogs.currencies.find((entry) => String(entry.id) === currencyId);
	const local = catalogs.currencies.find((entry) => entry.id === catalogs.localCurrencyId);
	const subtotal = lines.reduce((sum, line) => sum + (Number(line.quantity) || 0) * (Number(line.unitCost) || 0), 0);
	// What is missing, said under the buttons instead of just greying them out.
	const problems: string[] = [];
	if (supplierId === "") problems.push("Falta el proveedor");
	if (warehouseId === "") problems.push("Falta la bodega donde llega");
	if (lines.length < 1) problems.push("Agrega al menos un producto");
	if (foreign && !(Number(exchangeRate) > 0)) problems.push("Falta el tipo de cambio (" + (local?.code || "") + " por " + (currency?.code || "") + ")");
	lines.forEach((line) => {
		if (!isWhole(line.quantity) || !(Number(line.quantity) > 0)) problems.push(line.name + ": la cantidad va en piezas enteras");
		if (line.unitCost.trim() === "" || !(Number(line.unitCost) >= 0)) problems.push(line.name + ": falta el costo unitario");
	});
	const valid = problems.length < 1;
	const submitProblems = expectedAt === "" ? [...problems, "Para enviar falta la fecha de llegada"] : problems;
	// A tap on the dark border (or Escape) used to throw away the products already captured without asking.
	const tryClose = () => {
		if (busy) return;
		if (lines.length < 1 || window.confirm("¿Salir sin guardar? Se pierden los productos que capturaste.")) onClose();
	};

	const patchLine = (index: number, patch: Partial<FormLine>) => setLines((current) => current.map((line, i) => i === index ? { ...line, ...patch } : line));

	const save = async (submit: boolean) => {
		setBusy(true);
		setError(null);
		try {
			const response = await HttpManager.getInstance().purchaseService.save(campusId, order ? order.id : null, {
				supplierId, warehouseId, currencyId, exchangeRate: foreign ? exchangeRate : 1, expectedAt, supplierReference: reference.trim(), notes: notes.trim(),
				submit: submit ? 1 : 0,
				lines: lines.map((line) => ({ articleId: line.articleId, quantity: Number(line.quantity), unitCost: Number(line.unitCost) }))
			}, files);
			onSaved(response.data.order, submit ? response.data.order.folio + " enviada a Contraloría" : response.data.message);
		} catch (requestError: any) {
			setError(errorOf(requestError, "No se pudo guardar la orden."));
		} finally {
			setBusy(false);
		}
	};

	return <Modal width="860px" busy={busy} onClose={tryClose}>
		<h4 style={{ marginBottom: "12px" }}>{order ? "Editar " + order.folio : "Nueva orden de compra"}</h4>
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
			<div style={ui.field("260px")}>
				<span style={ui.caption}>Proveedor <span style={{ color: "var(--wa-oro)" }}>*</span></span>
				<div style={{ display: "flex", gap: "6px" }}>
					<select className="form-select" style={ui.input} value={supplierId} disabled={busy} onChange={(event) => setSupplierId(event.target.value)}>
						<option value="">Elige…</option>
						{catalogs.suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
					</select>
					<button type="button" className={ui.btn} disabled={busy} onClick={onNewSupplier} title="Nuevo proveedor">+</button>
				</div>
			</div>
			<div style={ui.field("200px")}>
				<span style={ui.caption}>Llega a</span>
				<select className="form-select" style={ui.input} value={warehouseId} disabled={busy} onChange={(event) => setWarehouseId(event.target.value)}>
					{catalogs.warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
				</select>
			</div>
			<div style={ui.field("150px")}>
				<span style={ui.caption}>Fecha de llegada <span style={{ color: "var(--wa-oro)" }}>*</span></span>
				<input type="date" className="form-control" style={ui.input} value={expectedAt} disabled={busy} onChange={(event) => setExpectedAt(event.target.value)} />
			</div>
			<div style={ui.field("110px")}>
				<span style={ui.caption}>Moneda</span>
				<select className="form-select" style={ui.input} value={currencyId} disabled={busy} onChange={(event) => setCurrencyId(event.target.value)}>
					{catalogs.currencies.map((entry) => <option key={entry.id} value={entry.id}>{entry.code}</option>)}
				</select>
			</div>
			{foreign ? <div style={ui.field("150px")}>
				<span style={ui.caption}>Tipo de cambio ({local?.code} por {currency?.code})</span>
				<input type="number" min={0} step="0.0001" className="form-control" style={ui.input} value={exchangeRate} disabled={busy} onChange={(event) => setExchangeRate(event.target.value)} />
			</div> : null}
			<div style={ui.field("170px")}>
				<span style={ui.caption}>Factura o cotización</span>
				<input type="text" className="form-control" style={ui.input} maxLength={80} value={reference} disabled={busy} onChange={(event) => setReference(event.target.value)} />
			</div>
		</div>

		<div style={{ marginTop: "14px", ...ui.card, padding: "10px" }}>
			<div style={{ fontWeight: 700, marginBottom: "6px" }}>Productos</div>
			{lines.length > 0 ? <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "8px" }}>
				<thead><tr>
					<th style={ui.th}>Producto</th>
					<th style={{ ...ui.th, width: "90px" }}>Cantidad</th>
					<th style={{ ...ui.th, width: "130px" }}>Costo unitario ({currency?.code})</th>
					<th style={{ ...ui.th, ...ui.right, width: "120px" }}>Subtotal</th>
					<th style={{ ...ui.th, width: "30px" }}></th>
				</tr></thead>
				<tbody>{lines.map((line, index) => <tr key={line.articleId}>
					<td style={ui.td}><div style={{ fontWeight: 600 }}>{line.name}</div><div style={ui.tiny}>{line.waCode}</div></td>
					<td style={ui.td}><input type="number" min={1} step={1} inputMode="numeric" className="form-control" style={{ ...ui.input, borderColor: isWhole(line.quantity) ? undefined : "var(--wa-oro)" }} value={line.quantity} disabled={busy} onChange={(event) => patchLine(index, { quantity: event.target.value })} /></td>
					<td style={ui.td}><input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={line.unitCost} disabled={busy} onChange={(event) => patchLine(index, { unitCost: event.target.value })} /></td>
					<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{fmt(currency?.symbol || "$", (Number(line.quantity) || 0) * (Number(line.unitCost) || 0))}</td>
					<td style={ui.td}><button type="button" className="btn btn-sm btn-link" style={{ color: "var(--wa-apagado)", padding: 0 }} disabled={busy} onClick={() => setLines((current) => current.filter((_, i) => i !== index))}>✕</button></td>
				</tr>)}</tbody>
			</table> : null}
			<ProductPicker campusId={campusId} kinds={["article", "vitrina"]} excludeIds={lines.map((line) => line.articleId)} disabled={busy} groupVariants
				placeholder="Agregar producto: nombre, código o escanear…"
				onPick={(product) => {
					// Con tallas/colores siempre se pregunta cuántas de cada una (casi nunca se compra una sola talla).
					const modelId = product.variantCount > 0 ? product.id : product.modelId;
					if (modelId) { setError(null); setSizingModel(modelId); return; }
					if (!product.trackStock) { setError("\"" + product.name + "\" todavía no tiene tallas ni colores: dalas de alta en Productos."); return; }
					setError(null);
					setLines((current) => [...current, { articleId: product.id, name: product.name, waCode: product.waCode, quantity: "1", unitCost: "" }]);
				}} />
			<div style={{ textAlign: "right", marginTop: "8px", fontWeight: 700 }}>Total {fmt(currency?.symbol || "$", subtotal)} {currency?.code}
				{foreign && Number(exchangeRate) > 0 ? <span style={{ ...ui.small, fontWeight: 400 }}> · {fmt(local?.symbol || "$", subtotal * Number(exchangeRate))} {local?.code}</span> : null}
			</div>
		</div>

		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px" }}>
			<div style={{ flex: "1 1 260px" }}>
				<span style={ui.caption}>Factura o cotización del proveedor (foto o PDF)</span>
				<input type="file" accept="image/*,.pdf" multiple className="form-control" style={{ fontSize: "12px" }} disabled={busy}
					onChange={(event) => setFiles(event.target.files ? Array.from(event.target.files) : [])} />
			</div>
			<div style={{ flex: "1 1 260px" }}>
				<span style={ui.caption}>Notas</span>
				<textarea className="form-control" rows={2} maxLength={2000} value={notes} disabled={busy} onChange={(event) => setNotes(event.target.value)} style={{ fontSize: "13px", resize: "vertical" }} />
			</div>
		</div>

		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px", flexWrap: "wrap" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onClose}>Cancelar</button>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy || !valid} onClick={() => save(false)}>Guardar borrador</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid || expectedAt === ""} onClick={() => save(true)}>{busy ? "Guardando…" : "Enviar a Contraloría"}</button>
		</div>
		{!busy ? <Missing items={submitProblems} /> : null}
		{sizingModel !== null ? <ComprarPorTallas campusId={campusId} modelId={sizingModel} currencyCode={currency?.code || ""}
			inOrder={Object.fromEntries(lines.map((line) => [line.articleId, Number(line.quantity) || 0]))}
			onCancel={() => setSizingModel(null)} onAdd={(pieces: PickedPiece[], unitCost: string) => {
				setSizingModel(null);
				setLines((current) => {
					const next = current.map((line) => {
						const piece = pieces.find((entry) => entry.articleId === line.articleId);
						return piece ? { ...line, quantity: String((Number(line.quantity) || 0) + piece.quantity), unitCost: line.unitCost.trim() === "" ? unitCost : line.unitCost } : line;
					});
					pieces.filter((piece) => !current.some((line) => line.articleId === piece.articleId))
						.forEach((piece) => next.push({ articleId: piece.articleId, name: piece.name, waCode: piece.waCode, quantity: String(piece.quantity), unitCost }));
					return next;
				});
			}} /> : null}
	</Modal>;
};

// ------------------------------------------------------------------ detalle de la orden

type ActionKind = "approve" | "payment" | "receive" | "addCost" | "voidCost" | "schedule" | "reject" | "close" | "cancel" | "cancelLines" | "transferCredit" | "incident" | null;

const OrderModal: React.FC<{
	campusId: number; orderId: number; catalogs: PurchaseCatalogs | null;
	perms: { orders: any; approvals: any; receipts: any };
	onEdit: (order: OrderDetail) => void; onPay: (order: OrderDetail) => void; onChanged: (message: string) => void; onClose: () => void;
}> = ({ campusId, orderId, catalogs, perms, onEdit, onPay, onChanged, onClose }) => {
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const http = HttpManager.getInstance().purchaseService;
	const [order, setOrder] = useState<OrderDetail | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [action, setAction] = useState<ActionKind>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [actionError, setActionError] = useState<string | null>(null);
	const [voidingCost, setVoidingCost] = useState<number | null>(null);

	const load = useCallback(() => {
		http.get(campusId, orderId).then((response) => setOrder(response.data)).catch((requestError) => setError(errorOf(requestError, "No se pudo abrir la orden.")));
	}, [campusId, orderId]);
	useEffect(() => { load(); }, [load]);

	const run = async (call: () => Promise<any>, message: string) => {
		setBusy(true);
		setActionError(null);
		try {
			const response = await call();
			setOrder(response.data.order);
			setAction(null);
			onChanged(response.data.message || message);
		} catch (requestError: any) {
			setActionError(errorOf(requestError, "No se pudo completar."));
		} finally {
			setBusy(false);
		}
	};

	// En el visor de WAPP, sin otra ventana (Esteban, 2026-10-03).
	const openFile = (fileId: number) => {
		abrirArchivo({ origen: "Compras", nombre: "Archivo de la compra", cargar: () => http.file(campusId, orderId, fileId).then((response) => response.data) });
	};

	if (!order) return <Modal onClose={onClose}>{error ? <div className="alert alert-danger">{error}</div> : <Loading />}</Modal>;

	const st = order.statusId;
	const open = st === S.IN_TRANSIT || st === S.PARTLY;
	const isAuthor = currentUser?.id !== undefined && order.createdById === Number(currentUser.id);
	const isSuper = currentUser?.currentRole === "superAdmin";
	const canApprove = !!perms.approvals.create && st === S.PENDING && (!isAuthor || isSuper);
	const actions: { key: ActionKind | "edit" | "submit"; label: string; primary?: boolean }[] = [];
	if (st === S.DRAFT && perms.orders.create) { actions.push({ key: "edit", label: "Editar" }); actions.push({ key: "submit", label: "Enviar a Contraloría", primary: true }); }
	if (canApprove) { actions.push({ key: "reject", label: "Regresar a borrador" }); actions.push({ key: "approve", label: "Aprobar y registrar pago", primary: true }); }
	if (open && perms.receipts.create) actions.push({ key: "receive", label: "Recibir Mercancía", primary: true });
	if ([S.IN_TRANSIT, S.PARTLY, S.RECEIVED, S.CLOSED].includes(st) && perms.approvals.create && order.balance > 0) actions.push({ key: "payment", label: "Registrar pago" });
	if ([S.IN_TRANSIT, S.PARTLY, S.RECEIVED, S.CLOSED].includes(st) && perms.approvals.update && order.balance > 0) actions.push({ key: "schedule", label: "Programar pagos" });
	if (![S.DRAFT, S.CANCELLED].includes(st) && perms.approvals.create) actions.push({ key: "addCost", label: "Agregar costo" });
	if (open && perms.approvals.create) actions.push({ key: "cancelLines", label: "Cancelar piezas" });
	if (open && perms.approvals.create) actions.push({ key: "close", label: "Cerrar sin lo que falta" });
	if (order.supplierCredit > 0 && perms.approvals.create) actions.push({ key: "transferCredit", label: "Usar saldo a favor en otra orden" });
	if (![S.DRAFT, S.PENDING, S.CANCELLED].includes(st) && (perms.orders.create || perms.receipts.create)) actions.push({ key: "incident", label: "Reportar incidencia" });
	if ((([S.DRAFT, S.PENDING].includes(st) && perms.orders.delete) || (st === S.IN_TRANSIT && order.received === 0 && perms.approvals.create))) actions.push({ key: "cancel", label: "Cancelar Orden" });

	const onAction = (key: ActionKind | "edit" | "submit") => {
		setActionError(null);
		if (key === "edit") { onEdit(order); return; }
		if (key === "submit") { run(() => http.submit(campusId, order.id), "Enviada a Contraloría"); return; }
		if (key === "payment") { onPay(order); return; }
		setAction(key);
	};

	// Whoever only receives gets every amount as null (the server hides them): no money cards or columns for them.
	const seesMoney = (order.subtotal as number | null) !== null;
	const summary: [string, React.ReactNode][] = [
		["Llega a", order.warehouseName],
		["Fecha de llegada", <span style={{ color: order.late ? "var(--wa-oro)" : undefined, fontWeight: order.late ? 600 : 400 }}>{dmy(order.expectedAt)}{order.late ? " · atrasada" : ""}</span>]
	];
	if (seesMoney) {
		summary.push(["Total", <>{fmt(order.currencySymbol, order.subtotal)} {order.currencyCode}{order.exchangeRate !== null && order.exchangeRate !== 1 ? <div style={ui.tiny}>TC {order.exchangeRate}</div> : null}</>]);
		summary.push(["Costos adicionales", order.costsTotal > 0 ? fmt(order.localCurrencySymbol, order.costsTotal) : "-"]);
		summary.push(["Puesto en bodega", <>{fmt(order.localCurrencySymbol, order.totalLocal)} {order.localCurrencyCode}</>]);
		summary.push(["Pagado", <>{order.paid > 0 ? fmt(order.currencySymbol, order.paid) : "-"}{order.balance > 0 && order.paid > 0 ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>saldo {fmt(order.currencySymbol, order.balance)}</div> : null}</>]);
	}
	if (order.cancelledAmount > 0) summary.push(["Cancelado (eximido)", fmt(order.currencySymbol, order.cancelledAmount)]);
	if (order.creditNotes > 0) summary.push(["Notas de crédito", fmt(order.currencySymbol, order.creditNotes)]);
	if (order.supplierCredit > 0) summary.push(["Saldo a favor", <span style={{ color: "var(--wa-verde)" }}>{fmt(order.currencySymbol, order.supplierCredit)}</span>]);

	return <Modal width="920px" busy={busy} onClose={onClose}>
		<div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap", alignItems: "flex-start" }}>
			<div>
				<h4 style={{ margin: 0, fontFamily: "monospace" }}>{order.folio} <Chip statusId={st} text={order.status} /></h4>
				<div style={{ ...ui.small, marginTop: "4px" }}>{order.supplierName}{order.supplierReference ? " · " + order.supplierReference : ""} · creada {order.createdAt} por {order.createdBy}{order.approvedBy ? " · aprobada " + order.approvedAt + " por " + order.approvedBy : ""}</div>
			</div>
			<button type="button" className="btn-close" onClick={onClose} disabled={busy} />
		</div>
		{order.costsFlag ? <div className="alert" style={{ marginTop: "10px", padding: "6px 12px", fontSize: "13px", background: "var(--wa-oro-suave)", color: "var(--wa-oro)", border: "none" }}>
			Faltan flete o impuestos · {order.costsPendingDays} días desde la recepción
		</div> : null}
		{order.closeReason ? <div style={{ ...ui.small, marginTop: "6px" }}>Motivo de {st === S.CANCELLED ? "cancelación" : "cierre"}: {order.closeReason}</div> : null}

		<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "8px", marginTop: "12px" }}>
			{summary.map(([caption, value]) => <div key={caption} style={{ ...ui.card, padding: "8px 10px" }}>
				<div style={ui.caption}>{caption}</div><div style={{ fontWeight: 600, marginTop: "3px", fontSize: "14px" }}>{value}</div>
			</div>)}
		</div>

		{actions.length > 0 ? <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "12px" }}>
			{actions.map((entry) => <button key={String(entry.key)} type="button" disabled={busy}
				className={"btn btn-sm rounded-pill " + (entry.primary ? "btn-primary" : entry.key === "cancel" ? "btn-outline-secondary waOroTexto" : "btn-outline-secondary")}
				onClick={() => onAction(entry.key)}>{entry.label}</button>)}
		</div> : null}
		{st === S.PENDING && perms.approvals.create && isAuthor && !isSuper ? <div style={{ ...ui.small, marginTop: "6px" }}>La aprueba otra persona de Contraloría.</div> : null}
		{actionError && action === null ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{actionError}</div> : null}

		<div className="waScrollX" style={{ overflowX: "auto", marginTop: "14px" }}>
			<table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr>
					<th style={ui.th}>Producto</th>
					<th style={{ ...ui.th, ...ui.right }}>Pedido</th>
					{seesMoney ? <th style={{ ...ui.th, ...ui.right }}>Costo</th> : null}
					<th style={{ ...ui.th, ...ui.right }}>Recibido</th>
					<th style={{ ...ui.th, ...ui.right }}>Falta</th>
					{seesMoney ? <th style={{ ...ui.th, ...ui.right }}>Puesto en bodega</th> : null}
				</tr></thead>
				<tbody>{order.lines.map((line) => <tr key={line.id}>
					<td style={{ ...ui.td, overflowWrap: "anywhere" }}><div style={{ fontWeight: 600 }}>{line.name}</div><div style={ui.tiny}>{line.waCode}</div></td>
					<td style={{ ...ui.td, ...ui.right }}>{line.quantity}</td>
					{seesMoney ? <td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{fmt(order.currencySymbol, line.unitCost)}</td> : null}
					<td style={{ ...ui.td, ...ui.right }}>{line.received}{line.damaged > 0 ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>{line.damaged} dañadas</div> : null}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: line.pending > 0 ? 700 : 400 }}>{line.pending || "-"}{line.cancelled > 0 ? <div style={ui.tiny}>{line.cancelled} canceladas</div> : null}</td>
					{seesMoney ? <td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{line.landedUnitCost !== null ? fmt(order.localCurrencySymbol, line.landedUnitCost) : "-"}</td> : null}
				</tr>)}</tbody>
			</table>
		</div>

		{order.costs.length > 0 ? <Section title="Costos adicionales">
			{order.costs.map((cost) => <div key={cost.id} style={{ fontSize: "13px", padding: "4px 0", borderBottom: "1px solid var(--wa-filo)", opacity: cost.voided ? 0.55 : 1 }}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
					<span style={{ textDecoration: cost.voided ? "line-through" : undefined }}>
						<b>{cost.typeName}</b> · factura {cost.reference || "-"}{cost.vendor ? " · " + cost.vendor : ""}
						{cost.shared ? <span style={{ ...ui.tiny, color: "var(--wa-acento-texto)", fontWeight: 600, marginLeft: "6px" }}>compartida</span> : null}
					</span>
					<span style={{ display: "flex", gap: "6px", alignItems: "center" }}>
						<span style={{ fontWeight: 600, textDecoration: cost.voided ? "line-through" : undefined }}>{fmt(order.localCurrencySymbol, cost.amount)}</span>
						{cost.files.map((file) => <button key={file.id} type="button" className="btn btn-link btn-sm" style={{ padding: "0 2px" }} onClick={() => openFile(file.id)}>Factura</button>)}
						{!cost.voided && perms.approvals.create ? <button type="button" className="btn btn-link btn-sm" style={{ padding: "0 2px", color: "var(--wa-oro)" }} disabled={busy}
							onClick={() => { setActionError(null); setVoidingCost(cost.id); setAction("voidCost"); }}>Anular</button> : null}
					</span>
				</div>
				<div style={ui.small}>{cost.createdAt} · {cost.author}{cost.note ? " · " + cost.note : ""}{cost.voided ? " · anulado: " + (cost.voidReason || "") : ""}</div>
			</div>)}
		</Section> : null}

		{order.installments.length > 0 ? <Section title={order.credit ? "Calendario de pagos (crédito)" : "Calendario de pagos"}>
			{order.installments.map((row) => <div key={row.id} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "3px 0", borderBottom: "1px solid var(--wa-filo)" }}>
				<span>{row.dueDate}{row.note ? <span style={ui.small}> · {row.note}</span> : null}</span>
				<span>
					{fmt(order.currencySymbol, row.amount)}
					{row.pending <= 0.001 ? <span style={{ ...ui.tiny, color: "var(--wa-verde)", fontWeight: 600, marginLeft: "8px" }}>pagado</span>
						: <span style={{ ...ui.tiny, color: row.overdue ? "var(--wa-oro)" : "var(--wa-apagado)", fontWeight: 600, marginLeft: "8px" }}>{row.overdue ? "vencido · " : ""}falta {fmt(order.currencySymbol, row.pending)}</span>}
				</span>
			</div>)}
		</Section> : null}

		{order.payments.length > 0 ? <Section title="Pagos al proveedor">
			{order.payments.map((payment) => <div key={payment.id} style={{ fontSize: "13px", padding: "4px 0", borderBottom: "1px solid var(--wa-filo)" }}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
					<span><b>{fmt(order.currencySymbol, payment.amount)}</b> · {payment.paidAt}{payment.reference ? " · " + payment.reference : ""} · {payment.author}
						{payment.kind === "credit_transfer" ? <span style={{ ...ui.tiny, color: "var(--wa-verde)", fontWeight: 600, marginLeft: "6px" }}>saldo a favor</span> : null}
						{payment.ocrStatus === "match" ? <span style={{ ...ui.tiny, color: "var(--wa-verde)", fontWeight: 600, marginLeft: "6px" }} title={payment.ocrSummary || undefined}>comprobante coincide</span>
							: payment.ocrStatus === "mismatch" ? <span style={{ ...ui.tiny, color: "var(--wa-oro)", fontWeight: 700, marginLeft: "6px" }} title={payment.ocrSummary || undefined}>revisar comprobante</span> : null}
						{payment.otherOrders.length > 0 && payment.kind !== "credit_transfer" ? <div style={ui.small}>parte de un pago de {fmt(order.currencySymbol, payment.paymentTotal)} que también cubre {payment.otherOrders.join(", ")}</div> : null}
					</span>
					<span>{payment.files.map((file) => <button key={file.id} type="button" className="btn btn-link btn-sm" style={{ padding: "0 4px" }} onClick={() => openFile(file.id)}>Comprobante</button>)}</span>
				</div>
				{payment.note ? <div style={ui.small}>{payment.note}</div> : null}
			</div>)}
		</Section> : null}

		{order.incidents.length > 0 ? <Section title="Incidencias con el proveedor">
			<IncidentList campusId={campusId} incidents={order.incidents} canResolve={!!perms.approvals.create} currencySymbol={order.currencySymbol} onChanged={() => { load(); onChanged("Incidencia actualizada"); }} />
		</Section> : null}

		{order.cancellations.length > 0 ? <Section title="Piezas canceladas">
			{order.cancellations.map((row, index) => <div key={index} style={{ fontSize: "13px", padding: "3px 0", borderBottom: "1px solid var(--wa-filo)" }}>
				<div style={{ display: "flex", justifyContent: "space-between" }}><span>{row.name} × {row.quantity}</span><span>se eximen {fmt(order.currencySymbol, row.amount)}</span></div>
				<div style={ui.small}>{row.createdAt} · {row.author} · {row.reason}</div>
			</div>)}
		</Section> : null}

		{order.receipts.length > 0 ? <Section title="Recepciones">
			{order.receipts.map((receipt) => <div key={receipt.id} style={{ fontSize: "13px", padding: "4px 0", borderBottom: "1px solid var(--wa-filo)" }}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
					<span>{receipt.createdAt} · {receipt.author}</span>
					<span>{receipt.files.map((file, index) => <button key={file.id} type="button" className="btn btn-link btn-sm" style={{ padding: "0 4px" }} onClick={() => openFile(file.id)}>Foto {index + 1}</button>)}</span>
				</div>
				<div style={ui.small}>{receipt.lines.map((line) => line.name + " " + line.quantity + (line.damaged > 0 ? " (+" + line.damaged + " dañadas)" : "")).join(" · ")}</div>
				{receipt.note ? <div style={ui.small}>{receipt.note}</div> : null}
			</div>)}
		</Section> : null}

		{order.files.length > 0 ? <Section title="Adjuntos">
			{order.files.map((file) => <button key={file.id} type="button" className="btn btn-link btn-sm" style={{ padding: "0 8px 0 0" }} onClick={() => openFile(file.id)}>{file.name}</button>)}
		</Section> : null}
		{order.notes ? <Section title="Notas"><div style={{ fontSize: "13px", whiteSpace: "pre-wrap" }}>{order.notes}</div></Section> : null}

		<Section title="Historial">
			{order.history.map((entry, index) => <div key={index} style={{ fontSize: "13px", padding: "3px 0" }}>
				<span style={ui.small}>{entry.createdAt}</span> · {entry.summary}
				{entry.note ? <div style={{ ...ui.small, paddingLeft: "10px" }}>{entry.note}</div> : null}
			</div>)}
		</Section>

		{action === "approve" ? <ApproveModal campusId={campusId} order={order} busy={busy} error={actionError} onCancel={() => setAction(null)}
			onConfirm={(payment, installments, files) => run(() => http.approve(campusId, order.id, payment, installments, files), "Aprobada")} /> : null}
		{action === "incident" ? <IncidentForm campusId={campusId} order={order} onCancel={() => setAction(null)} onDone={() => { setAction(null); load(); onChanged("Incidencia abierta"); }} /> : null}
		{action === "cancelLines" ? <CancelLinesModal order={order} busy={busy} error={actionError} onCancel={() => setAction(null)}
			onConfirm={(lines, reason) => run(() => http.cancelLines(campusId, order.id, lines, reason), "Cancelación parcial registrada")} /> : null}
		{action === "transferCredit" ? <TransferCreditModal campusId={campusId} order={order} busy={busy} error={actionError} onCancel={() => setAction(null)}
			onConfirm={(toOrderId, amount, note) => run(async () => { const response = await http.transferCredit(campusId, order.id, toOrderId, amount, note); return { data: { ...response.data, order: (await http.get(campusId, order.id)).data } }; }, "Saldo a favor aplicado")} /> : null}
		{action === "schedule" ? <ScheduleModal order={order} busy={busy} error={actionError} onCancel={() => setAction(null)}
			onConfirm={(installments, note) => run(() => http.reschedule(campusId, order.id, installments, note), "Pagos programados")} /> : null}
		{action === "addCost" && catalogs ? <AddCostModal order={order} catalogs={catalogs} busy={busy} error={actionError} onCancel={() => setAction(null)}
			onConfirm={(data, files) => run(() => http.addCost(campusId, order.id, data, files), "Costo agregado")} /> : null}
		{action === "voidCost" && voidingCost !== null ? <ReasonPrompt maxLength={500} title="Anular costo" label="Motivo" confirmLabel="Anular" confirmClass="btn-primary"
			busy={busy} error={actionError} onCancel={() => setAction(null)} onConfirm={(reason) => run(() => http.voidCost(campusId, order.id, voidingCost, reason), "Costo anulado")} /> : null}
		{action === "receive" ? <ReceiveModal order={order} busy={busy} error={actionError} onCancel={() => setAction(null)}
			onConfirm={(lines, note, photos, barcodes) => run(() => http.receive(campusId, order.id, lines, note, photos, barcodes), "Recepción registrada")} /> : null}
		{action === "reject" ? <ReasonPrompt title={"Regresar " + order.folio + " a borrador"} label="Qué hay que corregir" confirmLabel="Regresar" confirmClass="btn-primary"
			busy={busy} error={actionError} onCancel={() => setAction(null)} onConfirm={(reason) => run(() => http.reject(campusId, order.id, reason), "Regresada a borrador")} /> : null}
		{action === "close" ? <ReasonPrompt maxLength={1000} title={"Cerrar " + order.folio} label="Por qué no va a llegar lo que falta" confirmLabel="Cerrar Orden" confirmClass="btn-primary"
			busy={busy} error={actionError} onCancel={() => setAction(null)} onConfirm={(reason) => run(() => http.close(campusId, order.id, reason), "Orden cerrada")} /> : null}
		{action === "cancel" ? <ReasonPrompt maxLength={1000} title={"Cancelar " + order.folio} label="Motivo" confirmLabel="Cancelar Orden" confirmClass="btn-primary"
			busy={busy} error={actionError} onCancel={() => setAction(null)}
			onConfirm={(reason) => run(() => http.cancel(campusId, order.id, reason, st === S.IN_TRANSIT || !perms.orders.delete), "Orden cancelada")} /> : null}
	</Modal>;
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) =>
	<div style={{ marginTop: "14px" }}>
		<div style={{ fontWeight: 700, fontSize: "14px", marginBottom: "4px" }}>{title}</div>
		{children}
	</div>;

const ReceiveModal: React.FC<{ order: OrderDetail; busy?: boolean; error?: string | null; onConfirm: (lines: { lineId: number; quantity: number; damaged: number }[], note: string, photos: File[], barcodes: { lineId: number; barcode: string }[]) => void; onCancel: () => void }> = ({ order, busy, error, onConfirm, onCancel }) => {
	const pendingLines = order.lines.filter((line) => line.pending > 0);
	const narrow = useNarrow();
	// Código escaneado que no está en la orden: quien recibe dice de cuál renglón es y se le guarda al producto al registrar.
	const [unknownCode, setUnknownCode] = useState<string | null>(null);
	const [newCodes, setNewCodes] = useState<Record<number, string>>({});
	const [counts, setCounts] = useState<Record<number, { good: string; damaged: string }>>({});
	const [scan, setScan] = useState<string>("");
	const [scanNote, setScanNote] = useState<{ text: string; warn: boolean } | null>(null);
	const [note, setNote] = useState<string>("");
	const [photos, setPhotos] = useState<File[]>([]);
	const [serials, setSerials] = useState<Record<number, string>>({});
	const scanRef = useRef<HTMLInputElement>(null);
	/** What each quantity box had when it got the focus (to undo a code the reader typed into it). */
	const focusValue = useRef<Record<string, string>>({});

	// With a mouse (laptop + USB reader) "Escanear" keeps the focus so the next piece goes there; on a phone, focusing it
	// would open the keyboard over the table (QA 2026-10-19), so touch screens are left alone.
	const refocus = () => { if (finePointer()) window.setTimeout(() => scanRef.current?.focus(), 0); };
	useEffect(() => { refocus(); }, []);

	type Counts = Record<number, { good: string; damaged: string }>;
	const set = (lineId: number, patch: Partial<{ good: string; damaged: string }>) =>
		setCounts((current) => ({ ...current, [lineId]: { good: current[lineId]?.good || "", damaged: current[lineId]?.damaged || "", ...patch } }));
	const serialList = (lineId: number): string[] => (serials[lineId] || "").split(/[\s,;]+/).filter(Boolean);

	/** One more good piece on the line. A piece over what is pending is not added and is said out loud (it used to vanish). */
	const addPiece = (line: OrderLine, base: Counts, suffix = ""): boolean => {
		const good = Number(base[line.id]?.good) || 0;
		const damaged = Number(base[line.id]?.damaged) || 0;
		if (good + damaged + 1 > line.pending) {
			setScanNote({ text: line.name + ": ya están las " + line.pending + " que faltaban. Esta pieza sobra: apártala y repórtala como incidencia.", warn: true });
			return false;
		}
		set(line.id, { good: String(good + 1) });
		setScanNote({ text: line.name + ": " + (good + 1) + " de " + line.pending + suffix, warn: false });
		return true;
	};

	/** Serialized products: what is scanned and is not the product's code is the serial number of that piece. */
	const addSerial = (line: OrderLine, code: string, base: Counts) => {
		const list = serialList(line.id);
		if (list.some((entry) => entry.toLowerCase() === code.toLowerCase())) {
			setScanNote({ text: "La serie " + code + " ya está anotada en " + line.name + ".", warn: true });
			return;
		}
		if (addPiece(line, base, " · serie " + code)) setSerials((current) => ({ ...current, [line.id]: [...list, code].join(", ") }));
	};

	// El lector escribe el código y manda Enter: suma una pieza al renglón que coincide.
	const processCode = (raw: string, base: Counts = counts) => {
		const typed = raw.trim();
		const code = typed.toLowerCase();
		if (!code) return;
		const line = pendingLines.find((entry) => (entry.barcode || "").toLowerCase() === code || (entry.waCode || "").toLowerCase() === code || (newCodes[entry.id] || "").toLowerCase() === code);
		if (line) {
			setUnknownCode(null);
			addPiece(line, base);
			return;
		}
		const free = pendingLines.filter((entry) => !entry.serialized && !entry.barcode && !newCodes[entry.id]);
		const serialLines = pendingLines.filter((entry) => entry.serialized);
		if (free.length < 1 && serialLines.length < 1) {
			setUnknownCode(null);
			setScanNote({ text: "\"" + typed + "\" no está en esta orden.", warn: true });
			return;
		}
		if (free.length < 1 && serialLines.length === 1) {
			setUnknownCode(null);
			addSerial(serialLines[0], typed, base);
			return;
		}
		setScanNote(null);
		setUnknownCode(typed);
	};
	const onScan = () => { const value = scan; setScan(""); processCode(value); };

	/** A reader that "types" into a quantity box (long number + Enter): put the box back and treat it as a scan. */
	const quantityKeyDown = (line: OrderLine, field: "good" | "damaged") => (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== "Enter") return;
		event.preventDefault();
		const typed = event.currentTarget.value;
		const before = focusValue.current[field + line.id] ?? "";
		if (typed.length - before.length < 6) return;
		const restored = { good: field === "good" ? before : counts[line.id]?.good || "", damaged: field === "damaged" ? before : counts[line.id]?.damaged || "" };
		set(line.id, restored);
		processCode(typed.startsWith(before) ? typed.slice(before.length) : typed, { ...counts, [line.id]: restored });
		refocus();
	};

	const payload = pendingLines.map((line) => ({ lineId: line.id, quantity: Number(counts[line.id]?.good) || 0, damaged: Number(counts[line.id]?.damaged) || 0,
		serials: serialList(line.id) }))
		.filter((line) => line.quantity + line.damaged > 0);
	const problems: string[] = [];
	if (payload.length < 1) problems.push("Anota cuántas piezas llegaron");
	pendingLines.forEach((line) => {
		const good = counts[line.id]?.good;
		const damaged = counts[line.id]?.damaged;
		if (!isWhole(good) || !isWhole(damaged)) { problems.push(line.name + ": las cantidades van en piezas enteras"); return; }
		const sum = (Number(good) || 0) + (Number(damaged) || 0);
		if (sum > line.pending) problems.push(line.name + ": anotaste " + sum + " y solo faltan " + line.pending);
		if (line.serialized && (Number(good) || 0) > 0 && serialList(line.id).length !== (Number(good) || 0)) problems.push(line.name + ": van " + (Number(good) || 0) + " números de serie y llevas " + serialList(line.id).length);
	});
	if (photos.length < 2) problems.push(photos.length === 1 ? "Falta 1 foto" : "Faltan las 2 fotos");
	const valid = problems.length < 1;

	const over = (line: OrderLine): boolean => (Number(counts[line.id]?.good) || 0) + (Number(counts[line.id]?.damaged) || 0) > line.pending;
	const codesOf = (line: OrderLine) => <div style={{ ...ui.tiny, overflowWrap: "anywhere" }}>{line.waCode}{line.barcode ? " · " + line.barcode : ""}{!line.barcode && newCodes[line.id] ? <> · código nuevo {newCodes[line.id]} <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "11px" }} disabled={busy}
		onClick={() => { const rest = { ...newCodes }; delete rest[line.id]; setNewCodes(rest); refocus(); }}>✕ quitar</button></> : null}</div>;
	const goodInput = (line: OrderLine) => <div style={{ display: "flex", gap: "4px" }}>
		<input type="number" min={0} max={line.pending} step={1} inputMode="numeric" className="form-control" style={{ ...ui.input, minWidth: 0, ...(over(line) || !isWhole(counts[line.id]?.good) ? { borderColor: "var(--wa-oro)" } : {}) }} value={counts[line.id]?.good || ""} disabled={busy}
			onFocus={() => { focusValue.current["good" + line.id] = counts[line.id]?.good || ""; }} onKeyDown={quantityKeyDown(line, "good")}
			onChange={(event) => set(line.id, { good: event.target.value })} />
		<button type="button" className={ui.btn} disabled={busy} title="Todo lo que falta" onClick={() => { set(line.id, { good: String(line.pending - (Number(counts[line.id]?.damaged) || 0)) }); refocus(); }}>✓</button>
	</div>;
	const damagedInput = (line: OrderLine) => <input type="number" min={0} step={1} inputMode="numeric" className="form-control" style={{ ...ui.input, minWidth: 0, ...(!isWhole(counts[line.id]?.damaged) ? { borderColor: "var(--wa-oro)" } : {}) }} value={counts[line.id]?.damaged || ""} disabled={busy}
		onFocus={() => { focusValue.current["damaged" + line.id] = counts[line.id]?.damaged || ""; }} onKeyDown={quantityKeyDown(line, "damaged")}
		onChange={(event) => set(line.id, { damaged: event.target.value })} />;
	const serialInput = (line: OrderLine) => line.serialized ? <input type="text" className="form-control" style={{ ...ui.input, marginTop: "4px", width: "100%" }} placeholder="Números de serie de las que llegaron bien, separados por coma"
		value={serials[line.id] || ""} disabled={busy} onChange={(event) => setSerials({ ...serials, [line.id]: event.target.value })} /> : null;

	return <Modal width="760px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "4px" }}>Recibir {order.folio}</h4>
		<div style={{ ...ui.small, marginBottom: "10px" }}>{order.supplierName} · {order.warehouseName}</div>
		<form style={ui.field("100%")} onSubmit={(event) => { event.preventDefault(); onScan(); }}>
			<span style={ui.caption}>Escanear</span>
			<input ref={scanRef} type="text" className="form-control" style={ui.input} value={scan} disabled={busy} placeholder="Código de barras, código WA o número de serie"
				autoComplete="off" onChange={(event) => setScan(event.target.value)} />
			{scanNote ? <span style={scanNote.warn ? { ...ui.small, color: "var(--wa-oro)", fontWeight: 600 } : ui.small}>{scanNote.text}</span> : null}
		</form>
		{unknownCode ? <div style={{ ...ui.card, padding: "10px 12px", marginTop: "8px", borderColor: "var(--wa-acento)" }}>
			<div style={{ fontWeight: 700, fontSize: "14px", overflowWrap: "anywhere" }}>El código {unknownCode} no está en esta orden. ¿De cuál producto es?</div>
			<div style={{ ...ui.small, margin: "2px 0 8px" }}>Si es un producto sin código, se le guarda al registrar la recepción. Si es una máquina con número de serie, se anota como su serie.</div>
			<div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
				{pendingLines.filter((entry) => !entry.serialized && !entry.barcode && !newCodes[entry.id]).map((entry) => <button key={entry.id} type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy}
					onClick={() => {
						setNewCodes({ ...newCodes, [entry.id]: unknownCode });
						addPiece(entry, counts, " · código guardado al registrar");
						setUnknownCode(null);
						refocus();
					}}>{entry.name}</button>)}
				{pendingLines.filter((entry) => entry.serialized).map((entry) => <button key={entry.id} type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy}
					onClick={() => { addSerial(entry, unknownCode, counts); setUnknownCode(null); refocus(); }}>{entry.name} (serie)</button>)}
				<button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => { setUnknownCode(null); setScanNote({ text: "\"" + unknownCode + "\" no está en esta orden.", warn: true }); refocus(); }}>Ninguno</button>
			</div>
		</div> : null}
		{narrow
			// Phone: one card per product, quantities side by side at full width (no horizontal scroll at 375 px).
			? <div style={{ marginTop: "10px" }}>{pendingLines.map((line) => <div key={line.id} style={{ ...ui.card, padding: "10px 12px", marginBottom: "8px" }}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
					<div style={{ minWidth: 0 }}><div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{line.name}</div>{codesOf(line)}</div>
					<div style={{ textAlign: "right", flex: "0 0 auto" }}><div style={ui.caption}>Falta</div><div style={{ fontWeight: 700 }}>{line.pending}</div></div>
				</div>
				<div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
					<div style={{ flex: "1 1 0", minWidth: 0 }}><span style={ui.caption}>Llegaron bien</span>{goodInput(line)}</div>
					<div style={{ flex: "1 1 0", minWidth: 0 }}><span style={ui.caption}>Dañadas</span>{damagedInput(line)}</div>
				</div>
				{serialInput(line)}
			</div>)}</div>
			: <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
				<thead><tr>
					<th style={ui.th}>Producto</th>
					<th style={{ ...ui.th, ...ui.right }}>Falta</th>
					<th style={{ ...ui.th, width: "110px" }}>Llegaron bien</th>
					<th style={{ ...ui.th, width: "100px" }}>Dañadas</th>
				</tr></thead>
				<tbody>{pendingLines.map((line) => <tr key={line.id}>
					<td style={{ ...ui.td, overflowWrap: "anywhere" }}><div style={{ fontWeight: 600 }}>{line.name}</div>{codesOf(line)}{serialInput(line)}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700 }}>{line.pending}</td>
					<td style={ui.td}>{goodInput(line)}</td>
					<td style={ui.td}>{damagedInput(line)}</td>
				</tr>)}</tbody>
			</table>}
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px" }}>
			<div style={{ flex: "1 1 260px", minWidth: 0 }}>
				<span style={ui.caption}>Fotos: bultos cerrados y contenido abierto <span style={{ color: "var(--wa-oro)" }}>* mínimo 2</span></span>
				<input type="file" accept="image/*" capture="environment" multiple className="form-control" style={{ fontSize: "12px" }} disabled={busy}
					onChange={(event) => { const picked = event.target.files ? Array.from(event.target.files) : []; setPhotos((current) => [...current, ...picked]); event.target.value = ""; }} />
				{photos.length > 0 ? <div style={{ ...ui.small, marginTop: "4px" }}>{photos.length} foto{photos.length === 1 ? "" : "s"} · <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "11px" }} onClick={() => setPhotos([])}>quitar</button></div> : null}
			</div>
			<div style={{ flex: "1 1 260px", minWidth: 0 }}>
				<span style={ui.caption}>Nota</span>
				<textarea className="form-control" rows={2} maxLength={2000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} style={{ fontSize: "13px", resize: "vertical" }} />
			</div>
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px", flexWrap: "wrap" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid} onClick={() => onConfirm(payload, note.trim(), photos, Object.entries(newCodes).map(([lineId, barcode]) => ({ lineId: Number(lineId), barcode })))}>{busy ? "Guardando…" : "Registrar Recepción"}</button>
		</div>
		{!busy ? <Missing items={problems} /> : null}
	</Modal>;
};

// ------------------------------------------------------------------ aprobación con crédito, calendario de pagos y costos

interface ScheduleRow { dueDate: string; amount: string }

const addDays = (iso: string, days: number): string => { const d = new Date(iso + "T12:00:00"); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
/** Mismo día de cada mes (si el mes es más corto, el último día). */
const addMonths = (iso: string, months: number): string => {
	const [year, month, day] = iso.split("-").map(Number);
	const target = new Date(Date.UTC(year, month - 1 + months, 1));
	const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
	target.setUTCDate(Math.min(day, last));
	return target.toISOString().slice(0, 10);
};
/** Today in the device's time zone (toISOString is UTC: after 6 pm in Mexico/Honduras it already said tomorrow). */
const todayIso = (): string => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

/** Pagos programados: lista editable más "repartir en N pagos cada D días". */
const ScheduleEditor: React.FC<{ order: OrderDetail; target: number; rows: ScheduleRow[]; busy?: boolean; onChange: (rows: ScheduleRow[]) => void }> = ({ order, target, rows, busy, onChange }) => {
	const [count, setCount] = useState<string>("3");
	const [frequency, setFrequency] = useState<string>("monthly");
	const [every, setEvery] = useState<string>("30");
	const [first, setFirst] = useState<string>(addDays(todayIso(), 30));
	const scheduled = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
	const split = () => {
		const n = Math.max(1, Math.min(36, Number(count) || 1));
		const base = Math.floor((target / n) * 100) / 100;
		const out: ScheduleRow[] = [];
		for (let i = 0; i < n; i++) {
			const amount = i === n - 1 ? Math.round((target - base * (n - 1)) * 100) / 100 : base;
			out.push({ dueDate: frequency === "monthly" ? addMonths(first, i) : addDays(first, i * (Number(every) || 30)), amount: amount.toFixed(2) });
		}
		onChange(out);
	};
	return <div style={{ ...ui.card, padding: "10px", marginTop: "10px" }}>
		<div style={{ fontWeight: 700, marginBottom: "6px" }}>Pagos programados</div>
		<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}>
			<div style={ui.field("80px")}><span style={ui.caption}>Pagos</span><input type="number" min={1} max={36} className="form-control" style={ui.input} value={count} disabled={busy} onChange={(event) => setCount(event.target.value)} /></div>
			<div style={ui.field("130px")}><span style={ui.caption}>Frecuencia</span>
				<select className="form-select" style={ui.input} value={frequency} disabled={busy} onChange={(event) => setFrequency(event.target.value)}>
					<option value="monthly">Mensual</option>
					<option value="days">Cada N días</option>
				</select></div>
			{frequency === "days" ? <div style={ui.field("100px")}><span style={ui.caption}>Días</span><input type="number" min={1} className="form-control" style={ui.input} value={every} disabled={busy} onChange={(event) => setEvery(event.target.value)} /></div> : null}
			<div style={ui.field("150px")}><span style={ui.caption}>Primer pago</span><input type="date" className="form-control" style={ui.input} value={first} disabled={busy} onChange={(event) => setFirst(event.target.value)} /></div>
			<button type="button" className={ui.btn} disabled={busy || target <= 0} onClick={split}>Repartir {fmt(order.currencySymbol, target)}</button>
		</div>
		{rows.map((row, index) => <div key={index} style={{ display: "flex", gap: "8px", marginTop: "6px", alignItems: "center" }}>
			<input type="date" className="form-control" style={{ ...ui.input, width: "160px" }} value={row.dueDate} min={todayIso()} disabled={busy}
				onChange={(event) => onChange(rows.map((entry, i) => i === index ? { ...entry, dueDate: event.target.value } : entry))} />
			<input type="number" min={0} step="0.01" className="form-control" style={{ ...ui.input, width: "150px" }} value={row.amount} disabled={busy}
				onChange={(event) => onChange(rows.map((entry, i) => i === index ? { ...entry, amount: event.target.value } : entry))} />
			<button type="button" className="btn btn-sm btn-link" style={{ color: "var(--wa-apagado)", padding: 0 }} disabled={busy} onClick={() => onChange(rows.filter((_, i) => i !== index))}>✕</button>
		</div>)}
		<div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", alignItems: "center" }}>
			<button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} disabled={busy} onClick={() => onChange([...rows, { dueDate: addDays(todayIso(), 30), amount: "" }])}>Agregar pago</button>
			<span style={{ ...ui.small, fontWeight: 600, color: Math.abs(scheduled - target) > 0.01 ? "var(--wa-oro)" : "var(--wa-verde)" }}>Programado {fmt(order.currencySymbol, scheduled)} de {fmt(order.currencySymbol, target)}</span>
		</div>
	</div>;
};

const ApproveModal: React.FC<{ campusId: number; order: OrderDetail; busy?: boolean; error?: string | null; onConfirm: (payment: Record<string, any>, installments: { dueDate: string; amount: number }[], files: File[]) => void; onCancel: () => void }> = ({ campusId, order, busy, error, onConfirm, onCancel }) => {
	const [mode, setMode] = useState<"cash" | "credit">(order.supplier?.paymentTerms ? "credit" : "cash");
	const [amount, setAmount] = useState<string>(String(order.subtotal));
	const [paidAt, setPaidAt] = useState<string>(todayIso());
	const [reference, setReference] = useState<string>("");
	const [rate, setRate] = useState<string>(order.exchangeRate !== 1 ? String(order.exchangeRate) : "");
	const [note, setNote] = useState<string>("");
	const [files, setFiles] = useState<File[]>([]);
	const [rows, setRows] = useState<ScheduleRow[]>([]);
	const foreign = order.currencyCode !== order.localCurrencyCode;
	const payNow = mode === "cash" ? Number(amount) || 0 : Number(amount) || 0;
	const remaining = Math.round((order.subtotal - payNow) * 100) / 100;
	const scheduled = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
	const valid = (payNow <= 0 || (files.length > 0 && reference.trim() !== "")) && Math.abs(payNow + scheduled - order.subtotal) <= 0.01 && payNow >= 0;
	useEffect(() => { setAmount(mode === "cash" ? String(order.subtotal) : "0"); setRows([]); }, [mode]);
	return <Modal width="620px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "4px" }}>Aprobar {order.folio}</h4>
		<div style={{ ...ui.small, marginBottom: "10px" }}>{order.supplierName}{order.supplier?.paymentTerms ? " · " + order.supplier.paymentTerms : ""} · total {fmt(order.currencySymbol, order.subtotal)} {order.currencyCode}</div>
		<div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
			<button type="button" className={"btn btn-sm rounded-pill " + (mode === "cash" ? "btn-primary" : "btn-outline-secondary")} disabled={busy} onClick={() => setMode("cash")}>Pago ahora</button>
			<button type="button" className={"btn btn-sm rounded-pill " + (mode === "credit" ? "btn-primary" : "btn-outline-secondary")} disabled={busy} onClick={() => setMode("credit")}>Crédito</button>
		</div>
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
			<div style={ui.field("140px")}><span style={ui.caption}>{mode === "credit" ? "Anticipo hoy" : "Pago hoy"} ({order.currencyCode})</span>
				<input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={amount} disabled={busy} onChange={(event) => setAmount(event.target.value)} /></div>
			{payNow > 0 ? <>
				<div style={ui.field("150px")}><span style={ui.caption}>Fecha del pago</span>
					<input type="date" className="form-control" style={ui.input} value={paidAt} disabled={busy} onChange={(event) => setPaidAt(event.target.value)} /></div>
				{foreign ? <div style={ui.field("140px")}><span style={ui.caption}>Tipo de cambio</span>
					<input type="number" min={0} step="0.0001" className="form-control" style={ui.input} value={rate} disabled={busy} onChange={(event) => setRate(event.target.value)} /></div> : null}
				<div style={ui.field("100%")}><span style={ui.caption}>Referencia (transferencia o recibo) <span style={{ color: "var(--wa-oro)" }}>*</span></span>
					<input type="text" className="form-control" style={ui.input} maxLength={100} value={reference} disabled={busy} onChange={(event) => setReference(event.target.value)} /></div>
				<ReceiptInput campusId={campusId} supplierId={order.supplierId} busy={busy} amount={payNow} currencySymbol={order.currencySymbol} onFiles={setFiles}
					onRead={(read) => { if (read.reference && !reference) setReference(read.reference); if (read.paid_on) setPaidAt(String(read.paid_on).slice(0, 10)); }} />
			</> : null}
		</div>
		{remaining > 0.01 ? <ScheduleEditor order={order} target={remaining} rows={rows} busy={busy} onChange={setRows} /> : null}
		<div style={{ ...ui.field("100%"), marginTop: "10px" }}><span style={ui.caption}>Nota</span>
			<textarea className="form-control" rows={2} maxLength={1000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} style={{ fontSize: "13px", resize: "vertical" }} /></div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid}
				onClick={() => onConfirm({ amount: payNow, paidAt, reference: reference.trim(), exchangeRate: foreign ? rate : undefined, note: note.trim() },
					rows.filter((row) => Number(row.amount) > 0).map((row) => ({ dueDate: row.dueDate, amount: Number(row.amount) })), files)}>
				{busy ? "Guardando…" : "Aprobar: queda en camino"}</button>
		</div>
	</Modal>;
};

const ScheduleModal: React.FC<{ order: OrderDetail; busy?: boolean; error?: string | null; onConfirm: (installments: { dueDate: string; amount: number }[], note: string) => void; onCancel: () => void }> = ({ order, busy, error, onConfirm, onCancel }) => {
	const pending = order.installments.filter((row: Installment) => row.pending > 0.001 && row.dueDateIso >= todayIso());
	const [rows, setRows] = useState<ScheduleRow[]>(pending.length > 0 ? pending.map((row) => ({ dueDate: row.dueDateIso, amount: row.pending.toFixed(2) })) : []);
	const [note, setNote] = useState<string>("");
	const scheduled = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
	const valid = rows.length > 0 && Math.abs(scheduled - order.balance) <= 0.01;
	return <Modal width="560px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "4px" }}>Programar pagos · {order.folio}</h4>
		<div style={ui.small}>Saldo {fmt(order.currencySymbol, order.balance)} {order.currencyCode}</div>
		<ScheduleEditor order={order} target={order.balance} rows={rows} busy={busy} onChange={setRows} />
		<div style={{ ...ui.field("100%"), marginTop: "10px" }}><span style={ui.caption}>Nota</span>
			<textarea className="form-control" rows={2} maxLength={1000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} style={{ fontSize: "13px", resize: "vertical" }} /></div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid}
				onClick={() => onConfirm(rows.map((row) => ({ dueDate: row.dueDate, amount: Number(row.amount) })), note.trim())}>{busy ? "Guardando…" : "Guardar calendario"}</button>
		</div>
	</Modal>;
};

const AddCostModal: React.FC<{ order: OrderDetail; catalogs: PurchaseCatalogs; busy?: boolean; error?: string | null; onConfirm: (data: Record<string, any>, files: File[]) => void; onCancel: () => void }> = ({ order, catalogs, busy, error, onConfirm, onCancel }) => {
	const [type, setType] = useState<string>("freight");
	const [amount, setAmount] = useState<string>("");
	const [reference, setReference] = useState<string>("");
	const [vendor, setVendor] = useState<string>("");
	const [shared, setShared] = useState<boolean>(false);
	const [note, setNote] = useState<string>("");
	const [files, setFiles] = useState<File[]>([]);
	const valid = Number(amount) > 0 && reference.trim() !== "" && files.length > 0;
	const active = order.costs.filter((cost) => !cost.voided);
	return <Modal width="560px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "4px" }}>Agregar costo · {order.folio}</h4>
		<div style={{ ...ui.small, marginBottom: "10px" }}>
			{active.length > 0 ? "Ya cargados: " + active.map((cost) => cost.typeName + " " + fmt(order.localCurrencySymbol, cost.amount) + " (" + (cost.reference || "-") + ")").join(" · ") : "Sin costos cargados"}
		</div>
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
			<div style={ui.field("220px")}><span style={ui.caption}>Tipo</span>
				<select className="form-select" style={ui.input} value={type} disabled={busy} onChange={(event) => setType(event.target.value)}>
					{catalogs.costTypes.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
				</select></div>
			<div style={ui.field("150px")}><span style={ui.caption}>Monto ({order.localCurrencyCode})</span>
				<input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={amount} disabled={busy} onChange={(event) => setAmount(event.target.value)} /></div>
			<div style={ui.field("48%")}><span style={ui.caption}>Factura del costo <span style={{ color: "var(--wa-oro)" }}>*</span></span>
				<input type="text" className="form-control" style={ui.input} maxLength={80} value={reference} disabled={busy} onChange={(event) => setReference(event.target.value)} /></div>
			<div style={ui.field("48%")}><span style={ui.caption}>A quién se pagó</span>
				<input type="text" className="form-control" style={ui.input} maxLength={120} value={vendor} disabled={busy} onChange={(event) => setVendor(event.target.value)} /></div>
			<div style={ui.field("100%")}><span style={ui.caption}>Factura o comprobante <span style={{ color: "var(--wa-oro)" }}>*</span></span>
				<input type="file" accept="image/*,.pdf" multiple className="form-control" style={{ fontSize: "12px" }} disabled={busy} onChange={(event) => setFiles(event.target.files ? Array.from(event.target.files) : [])} /></div>
			<div style={{ display: "flex", gap: "8px", alignItems: "center", cursor: "pointer" }} onClick={() => { if (!busy) setShared(!shared); }}>
				<input type="checkbox" className="form-check-input" checked={shared} readOnly style={{ margin: 0 }} />
				<span style={{ fontSize: "13px" }}>Factura compartida con otras órdenes (aquí va solo la parte de esta orden)</span>
			</div>
			<div style={ui.field("100%")}><span style={ui.caption}>Nota</span>
				<input type="text" className="form-control" style={ui.input} maxLength={255} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} /></div>
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid}
				onClick={() => onConfirm({ type, amount, reference: reference.trim(), vendor: vendor.trim(), note: note.trim(), shared: shared ? 1 : 0 }, files)}>{busy ? "Guardando…" : "Agregar costo"}</button>
		</div>
	</Modal>;
};

/** Comprobante de pago: al elegirlo, la IA lo lee y propone monto, referencia y fecha; avisa si no cuadra con lo capturado. */
const ReceiptInput: React.FC<{ campusId: number; supplierId: number | null; busy?: boolean; amount: number; currencySymbol: string;
	onFiles: (files: File[]) => void; onRead: (read: any) => void }> = ({ campusId, supplierId, busy, amount, currencySymbol, onFiles, onRead }) => {
	const [read, setRead] = useState<any | null>(null);
	const [reading, setReading] = useState<boolean>(false);
	const pick = async (files: File[]) => {
		onFiles(files);
		setRead(null);
		if (files.length < 1) return;
		setReading(true);
		try {
			const response = await HttpManager.getInstance().stockOpsService.readReceipt(campusId, files[0], supplierId);
			setRead(response.data.read);
			if (response.data.read) onRead(response.data.read);
		} catch {
			setRead(null);
		} finally {
			setReading(false);
		}
	};
	const amountOff = read && read.amount !== null && amount > 0 && Math.abs(Number(read.amount) - amount) > Math.max(1, amount * 0.01);
	return <div style={ui.field("100%")}>
		<span style={ui.caption}>Comprobante <span style={{ color: "var(--wa-oro)" }}>*</span></span>
		<input type="file" accept="image/*,.pdf" multiple className="form-control" style={{ fontSize: "12px" }} disabled={busy} onChange={(event) => pick(event.target.files ? Array.from(event.target.files) : [])} />
		{reading ? <span style={ui.small}>Leyendo el comprobante…</span> : null}
		{read ? <span style={{ ...ui.small, color: amountOff || !read.is_voucher ? "var(--wa-oro)" : "var(--wa-verde)", fontWeight: 600 }}>
			{!read.is_voucher ? "No parece un comprobante de pago." : read.summary}{amountOff ? " · el comprobante dice " + currencySymbol + Number(read.amount).toFixed(2) : ""}
		</span> : null}
	</div>;
};

// ------------------------------------------------------------------ pago a varias órdenes, cancelación de piezas, saldo a favor

/** Un pago real al proveedor repartido entre sus órdenes (misma moneda). */
const SupplierPaymentModal: React.FC<{ campusId: number; catalogs: PurchaseCatalogs; supplierId: number | null; orderId: number | null; onDone: (message: string) => void; onCancel: () => void }> = ({ campusId, catalogs, supplierId, orderId, onDone, onCancel }) => {
	const http = HttpManager.getInstance().purchaseService;
	const [supplier, setSupplier] = useState<string>(supplierId ? String(supplierId) : "");
	const [orders, setOrders] = useState<OrderSummary[] | null>(null);
	const [amounts, setAmounts] = useState<Record<number, string>>({});
	const [paidAt, setPaidAt] = useState<string>(todayIso());
	const [reference, setReference] = useState<string>("");
	const [rate, setRate] = useState<string>("");
	const [note, setNote] = useState<string>("");
	const [files, setFiles] = useState<File[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!supplier) { setOrders(null); return; }
		setOrders(null);
		http.openOrders(campusId, Number(supplier)).then((response) => {
			const payable = response.data.filter((row) => row.balance > 0.001);
			setOrders(payable);
			setAmounts(orderId ? { [orderId]: String(payable.find((row) => row.id === orderId)?.balance ?? "") } : {});
			if (payable[0] && payable[0].exchangeRate !== 1) setRate(String(payable[0].exchangeRate));
		}).catch((requestError) => { setOrders([]); setError(errorOf(requestError, "No se pudieron cargar las órdenes.")); });
	}, [supplier]);

	const chosen = (orders || []).filter((row) => Number(amounts[row.id]) > 0);
	const currencies = Array.from(new Set(chosen.map((row) => row.currencyCode)));
	const total = chosen.reduce((sum, row) => sum + Number(amounts[row.id]), 0);
	const over = chosen.some((row) => Number(amounts[row.id]) > row.balance + 0.001);
	const symbol = chosen[0]?.currencySymbol || "$";
	const foreign = chosen[0] ? chosen[0].currencyCode !== chosen[0].localCurrencyCode : false;
	const valid = chosen.length > 0 && currencies.length === 1 && !over && reference.trim() !== "" && files.length > 0;

	const submit = async () => {
		setBusy(true);
		setError(null);
		try {
			const response = await http.supplierPayment(campusId, { amount: total.toFixed(2), paidAt, reference: reference.trim(), exchangeRate: foreign ? rate : undefined, note: note.trim() },
				chosen.map((row) => ({ orderId: row.id, amount: Number(amounts[row.id]) })), files);
			onDone(response.data.message + " (" + response.data.orders.join(", ") + ")");
		} catch (requestError: any) {
			setError(errorOf(requestError, "No se pudo registrar el pago."));
		} finally {
			setBusy(false);
		}
	};

	return <Modal width="760px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "10px" }}>Registrar pago a proveedor</h4>
		<div style={ui.field("320px")}><span style={ui.caption}>Proveedor</span>
			<select className="form-select" style={ui.input} value={supplier} disabled={busy || supplierId !== null} onChange={(event) => setSupplier(event.target.value)}>
				<option value="">Elige…</option>
				{catalogs.suppliers.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
			</select></div>
		{supplier ? (orders === null ? <Loading /> : orders.length < 1 ? <Empty text="Este proveedor no tiene órdenes por pagar." /> : <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
			<thead><tr>
				<th style={{ ...ui.th, width: "30px" }}></th>
				<th style={ui.th}>Orden</th>
				<th style={ui.th}>Próximo vencimiento</th>
				<th style={{ ...ui.th, ...ui.right }}>Total</th>
				<th style={{ ...ui.th, ...ui.right }}>Debe</th>
				<th style={{ ...ui.th, width: "140px" }}>Este pago</th>
			</tr></thead>
			<tbody>{orders.map((row) => {
				const checked = amounts[row.id] !== undefined;
				return <tr key={row.id}>
					<td style={ui.td}><input type="checkbox" className="form-check-input" checked={checked} disabled={busy}
						onChange={(event) => { const next = { ...amounts }; if (event.target.checked) next[row.id] = String(row.balance); else delete next[row.id]; setAmounts(next); }} /></td>
					<td style={ui.td}><div style={{ fontFamily: "monospace", fontWeight: 700 }}>{row.folio}</div><div style={ui.tiny}>{row.status} · {row.currencyCode}</div></td>
					<td style={{ ...ui.td, color: row.nextDueOverdue ? "var(--wa-oro)" : undefined, fontWeight: row.nextDueOverdue ? 600 : 400 }}>{row.nextDue || "-"}{row.nextDueOverdue ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>vencido</div> : null}</td>
					<td style={{ ...ui.td, ...ui.right }}>{fmt(row.currencySymbol, row.subtotal)}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700 }}>{fmt(row.currencySymbol, row.balance)}</td>
					<td style={ui.td}>{checked ? <input type="number" min={0} step="0.01" className="form-control" style={Number(amounts[row.id]) > row.balance + 0.001 ? { ...ui.input, borderColor: "var(--wa-oro)" } : ui.input}
						value={amounts[row.id]} disabled={busy} onChange={(event) => setAmounts({ ...amounts, [row.id]: event.target.value })} /> : null}</td>
				</tr>;
			})}</tbody>
		</table>) : null}
		{currencies.length > 1 ? <div style={{ ...ui.small, color: "var(--wa-oro)", marginTop: "6px" }}>Un pago solo cubre órdenes en la misma moneda.</div> : null}
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px", alignItems: "flex-end" }}>
			<div style={{ ...ui.kpi, flex: "0 0 auto" }}><div style={ui.caption}>Total del pago</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{fmt(symbol, total)}</div></div>
			<div style={ui.field("150px")}><span style={ui.caption}>Fecha del pago</span>
				<input type="date" className="form-control" style={ui.input} value={paidAt} disabled={busy} onChange={(event) => setPaidAt(event.target.value)} /></div>
			{foreign ? <div style={ui.field("140px")}><span style={ui.caption}>Tipo de cambio</span>
				<input type="number" min={0} step="0.0001" className="form-control" style={ui.input} value={rate} disabled={busy} onChange={(event) => setRate(event.target.value)} /></div> : null}
			<div style={ui.field("220px")}><span style={ui.caption}>Referencia <span style={{ color: "var(--wa-oro)" }}>*</span></span>
				<input type="text" className="form-control" style={ui.input} maxLength={100} value={reference} disabled={busy} onChange={(event) => setReference(event.target.value)} /></div>
			<ReceiptInput campusId={campusId} supplierId={supplier ? Number(supplier) : null} busy={busy} amount={total} currencySymbol={symbol} onFiles={setFiles}
				onRead={(read) => { if (read.reference && !reference) setReference(read.reference); if (read.paid_on) setPaidAt(String(read.paid_on).slice(0, 10)); }} />
			<div style={ui.field("100%")}><span style={ui.caption}>Nota</span>
				<input type="text" className="form-control" style={ui.input} maxLength={1000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} /></div>
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid} onClick={submit}>{busy ? "Guardando…" : "Registrar pago"}</button>
		</div>
	</Modal>;
};

const CancelLinesModal: React.FC<{ order: OrderDetail; busy?: boolean; error?: string | null; onConfirm: (lines: { lineId: number; quantity: number }[], reason: string) => void; onCancel: () => void }> = ({ order, busy, error, onConfirm, onCancel }) => {
	const pendingLines = order.lines.filter((line) => line.pending > 0);
	const [counts, setCounts] = useState<Record<number, string>>({});
	const [reason, setReason] = useState<string>("");
	const payload = pendingLines.map((line) => ({ lineId: line.id, quantity: Number(counts[line.id]) || 0 })).filter((line) => line.quantity > 0);
	const over = pendingLines.some((line) => (Number(counts[line.id]) || 0) > line.pending);
	const exempted = pendingLines.reduce((sum, line) => sum + (Number(counts[line.id]) || 0) * line.unitCost, 0);
	const newTotal = order.subtotal - exempted;
	const valid = payload.length > 0 && !over && reason.trim().length >= 3;
	return <Modal width="680px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "4px" }}>Cancelar piezas · {order.folio}</h4>
		<div style={{ ...ui.small, marginBottom: "10px" }}>{order.supplierName}</div>
		<table style={{ width: "100%", borderCollapse: "collapse" }}>
			<thead><tr>
				<th style={ui.th}>Producto</th>
				<th style={{ ...ui.th, ...ui.right }}>Falta</th>
				<th style={{ ...ui.th, ...ui.right }}>Costo</th>
				<th style={{ ...ui.th, width: "120px" }}>Cancelar</th>
			</tr></thead>
			<tbody>{pendingLines.map((line) => <tr key={line.id}>
				<td style={ui.td}><div style={{ fontWeight: 600 }}>{line.name}</div><div style={ui.tiny}>{line.waCode}</div></td>
				<td style={{ ...ui.td, ...ui.right, fontWeight: 700 }}>{line.pending}</td>
				<td style={{ ...ui.td, ...ui.right }}>{fmt(order.currencySymbol, line.unitCost)}</td>
				<td style={ui.td}><div style={{ display: "flex", gap: "4px" }}>
					<input type="number" min={0} max={line.pending} className="form-control" style={(Number(counts[line.id]) || 0) > line.pending ? { ...ui.input, borderColor: "var(--wa-oro)" } : ui.input}
						value={counts[line.id] || ""} disabled={busy} onChange={(event) => setCounts({ ...counts, [line.id]: event.target.value })} />
					<button type="button" className={ui.btn} disabled={busy} title="Todo lo que falta" onClick={() => setCounts({ ...counts, [line.id]: String(line.pending) })}>✓</button>
				</div></td>
			</tr>)}</tbody>
		</table>
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
			<div style={ui.kpi}><div style={ui.caption}>Se exime</div><div style={{ fontSize: "18px", fontWeight: 700 }}>{fmt(order.currencySymbol, exempted)}</div></div>
			<div style={ui.kpi}><div style={ui.caption}>Total nuevo</div><div style={{ fontSize: "18px", fontWeight: 700 }}>{fmt(order.currencySymbol, newTotal)}</div></div>
			{order.paid > newTotal + 0.001 ? <div style={ui.kpi}><div style={ui.caption}>Queda saldo a favor</div><div style={{ fontSize: "18px", fontWeight: 700, color: "var(--wa-verde)" }}>{fmt(order.currencySymbol, order.paid - newTotal)}</div></div> : null}
		</div>
		<div style={{ ...ui.field("100%"), marginTop: "10px" }}><span style={ui.caption}>Motivo <span style={{ color: "var(--wa-oro)" }}>*</span></span>
			<textarea className="form-control" rows={2} maxLength={1000} value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)} style={{ fontSize: "13px", resize: "vertical" }} /></div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Regresar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid} onClick={() => onConfirm(payload, reason.trim())}>{busy ? "Guardando…" : "Cancelar piezas"}</button>
		</div>
	</Modal>;
};

const TransferCreditModal: React.FC<{ campusId: number; order: OrderDetail; busy?: boolean; error?: string | null; onConfirm: (toOrderId: number, amount: number, note: string) => void; onCancel: () => void }> = ({ campusId, order, busy, error, onConfirm, onCancel }) => {
	const [targets, setTargets] = useState<OrderSummary[] | null>(null);
	const [toId, setToId] = useState<string>("");
	const [amount, setAmount] = useState<string>("");
	const [note, setNote] = useState<string>("");
	useEffect(() => {
		HttpManager.getInstance().purchaseService.openOrders(campusId, order.supplierId)
			.then((response) => setTargets(response.data.filter((row) => row.id !== order.id && row.balance > 0.001 && row.currencyCode === order.currencyCode)))
			.catch(() => setTargets([]));
	}, []);
	const target = (targets || []).find((row) => String(row.id) === toId);
	useEffect(() => { if (target) setAmount(String(Math.min(order.supplierCredit, target.balance))); }, [toId]);
	const valid = target !== undefined && Number(amount) > 0 && Number(amount) <= Math.min(order.supplierCredit, target.balance) + 0.001;
	return <Modal width="520px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "4px" }}>Usar saldo a favor</h4>
		<div style={{ ...ui.small, marginBottom: "10px" }}>{order.folio} · saldo a favor {fmt(order.currencySymbol, order.supplierCredit)}</div>
		{targets === null ? <Loading /> : targets.length < 1 ? <Empty text="El proveedor no tiene otra orden con saldo pendiente en esta moneda." /> : <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
			<div style={ui.field("100%")}><span style={ui.caption}>Pasar a</span>
				<select className="form-select" style={ui.input} value={toId} disabled={busy} onChange={(event) => setToId(event.target.value)}>
					<option value="">Elige la orden…</option>
					{targets.map((row) => <option key={row.id} value={row.id}>{row.folio} · debe {fmt(row.currencySymbol, row.balance)}</option>)}
				</select></div>
			<div style={ui.field("160px")}><span style={ui.caption}>Monto</span>
				<input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={amount} disabled={busy} onChange={(event) => setAmount(event.target.value)} /></div>
			<div style={ui.field("100%")}><span style={ui.caption}>Nota</span>
				<input type="text" className="form-control" style={ui.input} maxLength={1000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} /></div>
		</div>}
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid} onClick={() => onConfirm(Number(toId), Number(amount), note.trim())}>{busy ? "Guardando…" : "Aplicar"}</button>
		</div>
	</Modal>;
};

/** Ficha del proveedor: a quién le compramos qué, cuánto, a qué precio, cuánto se le pagó y cuánto se le debe. */
const SupplierDetailModal: React.FC<{ campusId: number; supplierId: number; canPay: boolean; canEdit: boolean; onOpenOrder: (orderId: number) => void; onPay: (supplierId: number) => void; onEdit: (supplier: Supplier) => void; onClose: () => void }> = ({ campusId, supplierId, canPay, canEdit, onOpenOrder, onPay, onEdit, onClose }) => {
	const [detail, setDetail] = useState<SupplierDetail | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [tab, setTab] = useState<"orders" | "products" | "payments">("orders");
	useEffect(() => {
		HttpManager.getInstance().purchaseService.supplier(campusId, supplierId).then((response) => setDetail(response.data)).catch((requestError) => setError(errorOf(requestError, "No se pudo abrir el proveedor.")));
	}, [campusId, supplierId]);
	if (!detail) return <Modal onClose={onClose}>{error ? <div className="alert alert-danger">{error}</div> : <Loading />}</Modal>;
	const supplier = detail.supplier;
	const punctual = detail.punctuality.onTime + detail.punctuality.late;
	return <Modal width="980px" onClose={onClose}>
		<div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap", alignItems: "flex-start" }}>
			<div>
				<h4 style={{ margin: 0 }}>{supplier.name}</h4>
				<div style={{ ...ui.small, marginTop: "4px" }}>
					{[supplier.countryCode, supplier.taxId, supplier.contactName, supplier.phone, supplier.email, supplier.paymentTerms, supplier.leadTimeDays !== null ? "entrega típica " + supplier.leadTimeDays + " días" : null].filter(Boolean).join(" · ")}
				</div>
			</div>
			<div style={{ display: "flex", gap: "6px" }}>
				{canEdit ? <button type="button" className={ui.btn} onClick={() => onEdit(supplier)}>Editar Datos</button> : null}
				{canPay ? <button type="button" className={ui.btnPrimary} onClick={() => onPay(supplier.id)}>Registrar pago</button> : null}
				<button type="button" className="btn-close" onClick={onClose} />
			</div>
		</div>
		<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
			{detail.totals.map((total) => <React.Fragment key={total.currencyCode}>
				<div style={ui.kpi}><div style={ui.caption}>Comprado {total.currencyCode}</div><div style={{ fontSize: "18px", fontWeight: 700 }}>{fmt(total.currencySymbol, total.purchased)}</div></div>
				<div style={ui.kpi}><div style={ui.caption}>Pagado</div><div style={{ fontSize: "18px", fontWeight: 700 }}>{fmt(total.currencySymbol, total.paid)}</div></div>
				<div style={ui.kpi}><div style={ui.caption}>Por pagar</div><div style={{ fontSize: "18px", fontWeight: 700, color: total.balance > 0 ? "var(--wa-oro)" : undefined }}>{fmt(total.currencySymbol, total.balance)}</div></div>
				{total.credit > 0 ? <div style={ui.kpi}><div style={ui.caption}>Saldo a favor</div><div style={{ fontSize: "18px", fontWeight: 700, color: "var(--wa-verde)" }}>{fmt(total.currencySymbol, total.credit)}</div></div> : null}
			</React.Fragment>)}
			<div style={ui.kpi}><div style={ui.caption}>Puntualidad</div><div style={{ fontSize: "18px", fontWeight: 700 }}>{punctual > 0 ? Math.round(detail.punctuality.onTime * 100 / punctual) + "%" : "-"}</div>
				{detail.punctuality.late > 0 ? <div style={ui.tiny}>{detail.punctuality.late} tarde · {detail.punctuality.avgDaysLate} días en promedio</div> : null}</div>
		</div>
		<div style={ui.tabs}>
			{([["orders", "Órdenes (" + detail.orders.length + ")"], ["products", "Productos (" + detail.products.length + ")"], ["payments", "Pagos (" + detail.payments.length + ")"]] as [typeof tab, string][]).map(([key, label]) =>
				<button key={key} type="button" className={"btn btn-sm rounded-pill " + (tab === key ? "btn-primary" : "btn-outline-secondary")} onClick={() => setTab(key)}>{label}</button>)}
		</div>
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
			{tab === "orders" ? (detail.orders.length < 1 ? <Empty text="Sin órdenes todavía." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr><th style={ui.th}>Orden</th><th style={ui.th}>Llega</th><th style={{ ...ui.th, ...ui.right }}>Total</th><th style={{ ...ui.th, ...ui.right }}>Pagado</th><th style={{ ...ui.th, ...ui.right }}>Debe</th><th style={ui.th}>Estado</th></tr></thead>
				<tbody>{detail.orders.map((row) => <tr key={row.id} style={{ cursor: "pointer" }} onClick={() => onOpenOrder(row.id)}>
					<td style={ui.td}><div style={{ fontFamily: "monospace", fontWeight: 700 }}>{row.folio}</div><div style={ui.tiny}>{row.createdAt}</div></td>
					<td style={{ ...ui.td, color: row.late ? "var(--wa-oro)" : undefined }}>{dmy(row.expectedAt)}</td>
					<td style={{ ...ui.td, ...ui.right }}>{fmt(row.currencySymbol, row.subtotal)} <span style={ui.tiny}>{row.currencyCode}</span>{row.cancelledAmount > 0 ? <div style={ui.tiny}>cancelado {fmt(row.currencySymbol, row.cancelledAmount)}</div> : null}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.paid > 0 ? fmt(row.currencySymbol, row.paid) : "-"}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700, color: row.balance > 0 ? "var(--wa-oro)" : undefined }}>{row.balance > 0 ? fmt(row.currencySymbol, row.balance) : row.supplierCredit > 0 ? <span style={{ color: "var(--wa-verde)" }}>a favor {fmt(row.currencySymbol, row.supplierCredit)}</span> : "-"}</td>
					<td style={ui.td}><Chip statusId={row.statusId} text={row.status} /></td>
				</tr>)}</tbody>
			</table>)
			: tab === "products" ? (detail.products.length < 1 ? <Empty text="Todavía no se le ha comprado nada." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr><th style={ui.th}>Producto</th><th style={{ ...ui.th, ...ui.right }}>Órdenes</th><th style={{ ...ui.th, ...ui.right }}>Comprado</th><th style={{ ...ui.th, ...ui.right }}>Recibido</th><th style={{ ...ui.th, ...ui.right }}>Último precio</th><th style={ui.th}>Última compra</th></tr></thead>
				<tbody>{detail.products.map((row) => <tr key={row.articleId}>
					<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.name}</div><div style={ui.tiny}>{row.waCode}</div></td>
					<td style={{ ...ui.td, ...ui.right }}>{row.orders}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.quantity}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.received}</td>
					<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{fmt(row.lastCurrency, row.lastUnitCost)} <span style={ui.tiny}>{row.lastCurrencyCode}</span>
						{row.orders > 1 && Math.abs(row.lastUnitCost - row.firstUnitCost) > 0.001 ? <div style={{ ...ui.tiny, color: row.lastUnitCost > row.firstUnitCost ? "var(--wa-oro)" : "var(--wa-verde)" }}>antes {fmt(row.lastCurrency, row.firstUnitCost)}</div> : null}</td>
					<td style={ui.td}>{row.lastDate || "-"}</td>
				</tr>)}</tbody>
			</table>)
			: (detail.payments.length < 1 ? <Empty text="Sin pagos registrados." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr><th style={ui.th}>Fecha</th><th style={ui.th}>Referencia</th><th style={ui.th}>Órdenes</th><th style={{ ...ui.th, ...ui.right }}>Monto</th></tr></thead>
				<tbody>{detail.payments.map((row) => <tr key={row.id}>
					<td style={ui.td}>{row.paidAt}<div style={ui.tiny}>{row.author}</div></td>
					<td style={ui.td}>{row.reference}{row.kind === "credit_transfer" ? <div style={{ ...ui.tiny, color: "var(--wa-verde)" }}>saldo a favor</div> : null}</td>
					<td style={ui.td}>{row.orders.map((entry) => <div key={entry.orderId + "-" + entry.amount} style={{ fontSize: "12px" }}><span style={{ fontFamily: "monospace" }}>{entry.folio}</span> {fmt(row.currencySymbol, entry.amount)}</div>)}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700 }}>{row.kind === "credit_transfer" ? "-" : fmt(row.currencySymbol, row.amount)}</td>
				</tr>)}</tbody>
			</table>)}
		</div>
	</Modal>;
};

// ------------------------------------------------------------------ incidencias al proveedor

const IncidentsTab: React.FC<{ campusId: number; canResolve: boolean; onOpen: (orderId: number) => void }> = ({ campusId, canResolve, onOpen }) => {
	const [open, setOpen] = useState<boolean>(true);
	const [rows, setRows] = useState<Incident[] | null>(null);
	const load = useCallback(() => {
		HttpManager.getInstance().stockOpsService.incidents(campusId, { open: open ? 1 : 0 }).then((response) => setRows(response.data)).catch(() => setRows([]));
	}, [campusId, open]);
	useEffect(() => { load(); }, [load]);
	return <>
		<div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
			<button type="button" className={"btn btn-sm rounded-pill " + (open ? "btn-primary" : "btn-outline-secondary")} onClick={() => setOpen(true)}>Abiertas</button>
			<button type="button" className={"btn btn-sm rounded-pill " + (!open ? "btn-primary" : "btn-outline-secondary")} onClick={() => setOpen(false)}>Todas</button>
		</div>
		<div style={{ ...ui.card, padding: "6px 12px" }}>
			{rows === null ? <Loading /> : rows.length < 1 ? <Empty text="Sin incidencias." /> : <IncidentList campusId={campusId} incidents={rows} canResolve={canResolve} currencySymbol="$" onChanged={load} onOpen={onOpen} />}
		</div>
	</>;
};

const IncidentList: React.FC<{ campusId: number; incidents: Incident[]; canResolve: boolean; currencySymbol: string; onChanged: () => void; onOpen?: (orderId: number) => void }> = ({ campusId, incidents, canResolve, currencySymbol, onChanged, onOpen }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const [resolving, setResolving] = useState<Incident | null>(null);
	const [error, setError] = useState<string | null>(null);
	const claim = async (incident: Incident) => {
		try { await http.updateIncident(campusId, incident.id, { action: "claim" }); onChanged(); }
		catch (requestError: any) { setError(apiError(requestError, "No se pudo actualizar.")); }
	};
	return <>
		{error ? <div className="alert alert-danger" style={{ padding: "6px 10px", fontSize: "13px" }}>{error}</div> : null}
		{incidents.map((incident) => <div key={incident.id} style={{ fontSize: "13px", padding: "6px 0", borderBottom: "1px solid var(--wa-filo)" }}>
			<div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
				<span>
					<b>#{incident.id} {incident.typeName}</b>
					{onOpen ? <> · <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontFamily: "monospace" }} onClick={() => onOpen(incident.orderId)}>{incident.folio}</button> · {incident.supplierName}</> : null}
					<span style={{ ...ui.tiny, marginLeft: "6px", fontWeight: 600, color: incident.statusId <= 2 ? "var(--wa-oro)" : "var(--wa-verde)" }}>{incident.status}</span>
				</span>
				<span style={{ display: "flex", gap: "4px" }}>
					{incident.files.map((file, index) => <button key={file.id} type="button" className="btn btn-link btn-sm" style={{ padding: "0 2px" }}
						onClick={() => HttpManager.getInstance().purchaseService.file(campusId, incident.orderId, file.id).then((response) => openBlob(response.data, "Evidencia " + (index + 1))).catch(() => setError("No se pudo abrir el archivo."))}>Evidencia {index + 1}</button>)}
					{canResolve && incident.statusId === 1 ? <button type="button" className={ui.btn} onClick={() => claim(incident)}>Reclamado</button> : null}
					{canResolve && incident.statusId <= 2 ? <button type="button" className={ui.btnPrimary} onClick={() => setResolving(incident)}>Resolver</button> : null}
				</span>
			</div>
			<div style={ui.small}>{incident.description}{incident.lines.length ? " · " + incident.lines.map((line) => line.name + " " + line.quantity).join(", ") : ""}</div>
			<div style={ui.tiny}>{incident.createdAt} · {incident.createdBy}
				{incident.resolutionName ? " · " + incident.resolutionName + (incident.creditAmount ? " " + fmt(incident.currencySymbol || currencySymbol, Number(incident.creditAmount)) : "") + " · " + incident.resolvedBy + " " + incident.resolvedAt : ""}
				{incident.resolutionNote ? " · " + incident.resolutionNote : ""}</div>
		</div>)}
		{resolving ? <ResolveIncident campusId={campusId} incident={resolving} onCancel={() => setResolving(null)} onDone={() => { setResolving(null); onChanged(); }} /> : null}
	</>;
};

const IncidentForm: React.FC<{ campusId: number; order: OrderDetail; onDone: () => void; onCancel: () => void }> = ({ campusId, order, onDone, onCancel }) => {
	const [type, setType] = useState<string>("missing");
	const [description, setDescription] = useState<string>("");
	const [qty, setQty] = useState<Record<number, string>>({});
	const [files, setFiles] = useState<File[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const save = async () => {
		setBusy(true);
		setError(null);
		try {
			await HttpManager.getInstance().stockOpsService.createIncident(campusId, order.id, {
				type, description: description.trim(), lines: Object.entries(qty).filter(([, value]) => Number(value) > 0).map(([lineId, value]) => ({ lineId: Number(lineId), quantity: Number(value) }))
			}, files);
			onDone();
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo abrir la incidencia."));
		} finally {
			setBusy(false);
		}
	};
	return <Modal width="620px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "10px" }}>Incidencia · {order.folio}</h4>
		<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
			{[["missing", "Faltante"], ["damaged", "Dañado"], ["wrong", "Producto equivocado"], ["other", "Otro"]].map(([key, label]) =>
				<button key={key} type="button" className={"btn btn-sm rounded-pill " + (type === key ? "btn-primary" : "btn-outline-secondary")} disabled={busy} onClick={() => setType(key)}>{label}</button>)}
		</div>
		<table style={{ width: "100%", borderCollapse: "collapse" }}>
			<tbody>{order.lines.map((line) => <tr key={line.id}>
				<td style={ui.td}>{line.name}<div style={ui.tiny}>pedido {line.quantity} · recibido {line.received}{line.damaged ? " · " + line.damaged + " dañadas" : ""}</div></td>
				<td style={{ ...ui.td, width: "110px" }}><input type="number" min={0} className="form-control" style={ui.input} placeholder="Piezas" value={qty[line.id] || ""} disabled={busy} onChange={(event) => setQty({ ...qty, [line.id]: event.target.value })} /></td>
			</tr>)}</tbody>
		</table>
		<div style={{ ...ui.field("100%"), marginTop: "10px" }}><span style={ui.caption}>Qué pasó <span style={{ color: "var(--wa-oro)" }}>*</span></span>
			<textarea className="form-control" rows={2} maxLength={3000} value={description} disabled={busy} onChange={(event) => setDescription(event.target.value)} style={{ fontSize: "13px" }} /></div>
		<div style={{ ...ui.field("100%"), marginTop: "10px" }}><span style={ui.caption}>Evidencia: fotos, guía, correo del proveedor <span style={{ color: "var(--wa-oro)" }}>*</span></span>
			<input type="file" accept="image/*,.pdf" multiple className="form-control" style={{ fontSize: "12px" }} disabled={busy} onChange={(event) => setFiles(event.target.files ? Array.from(event.target.files) : [])} /></div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || description.trim().length < 5 || files.length < 1} onClick={save}>{busy ? "Guardando…" : "Abrir incidencia"}</button>
		</div>
	</Modal>;
};

const ResolveIncident: React.FC<{ campusId: number; incident: Incident; onDone: () => void; onCancel: () => void }> = ({ campusId, incident, onDone, onCancel }) => {
	const [resolution, setResolution] = useState<string>("credit_note");
	const [amount, setAmount] = useState<string>("");
	const [reference, setReference] = useState<string>("");
	const [note, setNote] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const save = async () => {
		setBusy(true);
		setError(null);
		try {
			await HttpManager.getInstance().stockOpsService.updateIncident(campusId, incident.id, { action: "resolve", resolution, creditAmount: resolution === "credit_note" ? amount : undefined, reference: reference.trim(), note: note.trim() });
			onDone();
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo resolver."));
		} finally {
			setBusy(false);
		}
	};
	return <Modal width="520px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "4px" }}>Resolver incidencia #{incident.id}</h4>
		<div style={{ ...ui.small, marginBottom: "10px" }}>{incident.typeName} · {incident.folio} · {incident.description}</div>
		<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
			{[["credit_note", "Nota de crédito"], ["replacement", "Reposición"], ["none", "Sin solución"]].map(([key, label]) =>
				<button key={key} type="button" className={"btn btn-sm rounded-pill " + (resolution === key ? "btn-primary" : "btn-outline-secondary")} disabled={busy} onClick={() => setResolution(key)}>{label}</button>)}
		</div>
		{resolution === "credit_note" ? <div style={{ display: "flex", gap: "10px" }}>
			<div style={ui.field("150px")}><span style={ui.caption}>Monto</span>
				<input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={amount} disabled={busy} onChange={(event) => setAmount(event.target.value)} /></div>
			<div style={ui.field("200px")}><span style={ui.caption}>Número de nota</span>
				<input type="text" className="form-control" style={ui.input} maxLength={80} value={reference} disabled={busy} onChange={(event) => setReference(event.target.value)} /></div>
		</div> : null}
		<div style={{ ...ui.field("100%"), marginTop: "10px" }}><span style={ui.caption}>Nota</span>
			<input type="text" className="form-control" style={ui.input} maxLength={2000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} /></div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || (resolution === "credit_note" && !(Number(amount) > 0))} onClick={save}>{busy ? "Guardando…" : "Resolver"}</button>
		</div>
	</Modal>;
};

// ------------------------------------------------------------------ por recibir y por pagar

const PendingLinesTab: React.FC<{ rows: PendingLine[]; loading: boolean; error: string | null; onOpen: (orderId: number) => void }> = ({ rows, loading, error, onOpen }) => {
	if (loading) return <div style={ui.card}><Loading /></div>;
	if (error) return <div className="alert alert-danger">{error}</div>;
	return <div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
		{rows.length < 1 ? <Empty text="No falta nada por recibir." />
			: <table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr>
					<th style={ui.th}>Producto</th>
					<th style={ui.th}>Orden</th>
					<th style={ui.th}>Llega</th>
					<th style={ui.th}>Bodega</th>
					<th style={{ ...ui.th, ...ui.right }}>Pedido</th>
					<th style={{ ...ui.th, ...ui.right }}>Recibido</th>
					<th style={{ ...ui.th, ...ui.right }}>Falta</th>
				</tr></thead>
				<tbody>{rows.map((row, index) => <tr key={index} style={{ cursor: "pointer" }} onClick={() => onOpen(row.orderId)}>
					<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.name}</div><div style={ui.tiny}>{row.waCode}</div></td>
					<td style={ui.td}><div style={{ fontFamily: "monospace", fontWeight: 700 }}>{row.folio}</div><div style={ui.tiny}>{row.supplierName}</div></td>
					<td style={{ ...ui.td, whiteSpace: "nowrap", color: row.late ? "var(--wa-oro)" : undefined, fontWeight: row.late ? 600 : 400 }}>{dmy(row.expectedAt)}{row.late ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>Atrasada</div> : null}</td>
					<td style={ui.td}>{row.warehouseName}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.ordered}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.received}{row.damaged > 0 ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>{row.damaged} dañadas</div> : null}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700 }}>{row.pending}</td>
				</tr>)}</tbody>
			</table>}
	</div>;
};

const PayablesTab: React.FC<{ rows: Payable[]; loading: boolean; error: string | null; onOpen: (orderId: number) => void; onPay?: (supplierId: number | null) => void }> = ({ rows, loading, error, onOpen, onPay }) => {
	if (loading) return <div style={ui.card}><Loading /></div>;
	if (error) return <div className="alert alert-danger">{error}</div>;
	const totals: Record<string, { symbol: string; overdue: number; week: number; later: number }> = {};
	rows.forEach((row) => {
		const key = row.currencyCode || "";
		totals[key] = totals[key] || { symbol: row.currencySymbol, overdue: 0, week: 0, later: 0 };
		if (row.overdue) totals[key].overdue += row.pending; else if (row.daysLeft <= 7) totals[key].week += row.pending; else totals[key].later += row.pending;
	});
	return <>
		{onPay ? <div style={{ display: "flex", justifyContent: "flex-end", margin: "0 0 8px" }}>
			<button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={() => onPay(null)}>Registrar pago a proveedor</button>
		</div> : null}
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
			{Object.entries(totals).map(([code, total]) => <React.Fragment key={code}>
				<div style={ui.kpi}><div style={ui.caption}>Vencido {code}</div><div style={{ fontSize: "20px", fontWeight: 700, color: total.overdue > 0 ? "var(--wa-oro)" : "var(--wa-tinta)" }}>{fmt(total.symbol, total.overdue)}</div></div>
				<div style={ui.kpi}><div style={ui.caption}>Próximos 7 días {code}</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{fmt(total.symbol, total.week)}</div></div>
				<div style={ui.kpi}><div style={ui.caption}>Después {code}</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{fmt(total.symbol, total.later)}</div></div>
			</React.Fragment>)}
		</div>
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
			{rows.length < 1 ? <Empty text="Nada pendiente de pagar a proveedores." />
				: <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Vence</th>
						<th style={ui.th}>Proveedor</th>
						<th style={ui.th}>Orden</th>
						<th style={{ ...ui.th, ...ui.right }}>Pago programado</th>
						<th style={{ ...ui.th, ...ui.right }}>Falta</th>
					</tr></thead>
					<tbody>{rows.map((row) => <tr key={row.orderId + "-" + row.id} style={{ cursor: "pointer" }} onClick={() => onOpen(row.orderId)}>
						<td style={{ ...ui.td, whiteSpace: "nowrap", color: row.overdue ? "var(--wa-oro)" : undefined, fontWeight: row.overdue ? 700 : 400 }}>
							{row.dueDate}<div style={row.overdue ? { ...ui.tiny, color: "var(--wa-oro)" } : ui.tiny}>{row.overdue ? "vencido hace " + Math.abs(row.daysLeft) + " días" : row.daysLeft === 0 ? "hoy" : "en " + row.daysLeft + " días"}</div>
						</td>
						<td style={ui.td}>{row.supplierName}{onPay ? <div><button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "11px" }}
							onClick={(event) => { event.stopPropagation(); onPay(row.supplierId); }}>Pagar a este proveedor</button></div> : null}</td>
						<td style={ui.td}><div style={{ fontFamily: "monospace", fontWeight: 700 }}>{row.folio}</div><div style={ui.tiny}>{row.status}</div></td>
						<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{fmt(row.currencySymbol, row.amount)} <span style={ui.tiny}>{row.currencyCode}</span></td>
						<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap", fontWeight: 700 }}>{fmt(row.currencySymbol, row.pending)}</td>
					</tr>)}</tbody>
				</table>}
		</div>
	</>;
};

// ------------------------------------------------------------------ qué comprar

const NeedsTab: React.FC<{ needs: PurchaseNeed[]; loading: boolean; error: string | null; canCreate: boolean; onCreate: (items: { articleId: number; name: string; waCode: string | null; quantity: number }[]) => void }> = ({ needs, loading, error, canCreate, onCreate }) => {
	const [picked, setPicked] = useState<number[]>([]);
	const buyable = needs.filter((need) => need.toBuy > 0 && !need.hasSizes);
	useEffect(() => { setPicked(buyable.map((need) => need.articleId)); }, [needs]);
	if (loading) return <div style={ui.card}><Loading /></div>;
	if (error) return <div className="alert alert-danger">{error}</div>;
	return <>
		{canCreate && buyable.length > 0 ? <div style={{ display: "flex", justifyContent: "flex-end", margin: "0 0 8px" }}>
			<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={picked.length < 1}
				onClick={() => onCreate(needs.filter((need) => picked.includes(need.articleId)).map((need) => ({ articleId: need.articleId, name: need.name, waCode: need.waCode, quantity: need.toBuy })))}>
				Crear orden con {picked.length} producto{picked.length === 1 ? "" : "s"}
			</button>
		</div> : null}
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
			{needs.length < 1 ? <Empty text="Todo lo vendido tiene existencia o ya viene en camino." />
				: <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						{canCreate ? <th style={{ ...ui.th, width: "30px" }}></th> : null}
						<th style={ui.th}>Pieza</th>
						<th style={{ ...ui.th, ...ui.right }}>Vendido sin entregar</th>
						<th style={{ ...ui.th, ...ui.right }}>En bodega</th>
						<th style={{ ...ui.th, ...ui.right }}>En camino</th>
						<th style={{ ...ui.th, ...ui.right }}>Por pedir</th>
					</tr></thead>
					<tbody>{needs.map((need) => <tr key={need.articleId}>
						{canCreate ? <td style={ui.td}>{need.toBuy > 0 && !need.hasSizes ? <input type="checkbox" className="form-check-input" checked={picked.includes(need.articleId)}
							onChange={(event) => setPicked((current) => event.target.checked ? [...current, need.articleId] : current.filter((id) => id !== need.articleId))} /> : null}</td> : null}
						<td style={ui.td}><div style={{ fontWeight: 600 }}>{need.name}</div><div style={ui.tiny}>{need.waCode}{need.hasSizes ? " · por talla" : ""}</div></td>
						<td style={{ ...ui.td, ...ui.right }}>{need.needed}{need.partial > 0 ? <div style={ui.tiny}>{need.partial} con abono</div> : null}</td>
						<td style={{ ...ui.td, ...ui.right }}>{need.onHand}</td>
						<td style={{ ...ui.td, ...ui.right }}>{need.inTransit || "-"}{need.eta ? <div style={ui.tiny}>llega {need.eta}</div> : null}</td>
						<td style={{ ...ui.td, ...ui.right, fontWeight: 700, color: need.toBuy > 0 ? "var(--wa-acento)" : "var(--wa-verde)" }}>{need.toBuy > 0 ? need.toBuy : "Cubierto"}</td>
					</tr>)}</tbody>
				</table>}
		</div>
	</>;
};

// ------------------------------------------------------------------ proveedores

/** New supplier = purchaseOrders C (POST); editing one = purchaseOrders U (PUT), same as the server asks. */
const SuppliersTab: React.FC<{ campusId: number; catalogs: PurchaseCatalogs | null; canCreate: boolean; canEdit: boolean; reloadKey: number; onEdit: (supplier: Supplier | null) => void; onOpen: (supplierId: number) => void }> = ({ campusId, catalogs, canCreate, canEdit, reloadKey, onEdit, onOpen }) => {
	const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
	useEffect(() => {
		HttpManager.getInstance().purchaseService.suppliers(campusId).then((response) => setSuppliers(response.data)).catch(() => setSuppliers([]));
	}, [campusId, reloadKey]);
	const currencyCode = (id: number | null) => catalogs?.currencies.find((entry) => entry.id === id)?.code || "-";
	return <>
		{canCreate ? <div style={{ display: "flex", justifyContent: "flex-end", margin: "0 0 8px" }}>
			<button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={() => onEdit(null)}>Nuevo Proveedor</button>
		</div> : null}
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
			{suppliers === null ? <Loading /> : suppliers.length < 1 ? <Empty text="Sin proveedores." />
				: <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Proveedor</th>
						<th style={ui.th}>País</th>
						<th style={ui.th}>Moneda</th>
						<th style={ui.th}>Contacto</th>
						<th style={ui.th}>Condiciones</th>
						<th style={{ ...ui.th, ...ui.right }}>Entrega típica</th>
						<th style={{ ...ui.th, ...ui.right }}>Órdenes</th>
						<th style={ui.th}></th>
					</tr></thead>
					<tbody>{suppliers.map((supplier) => <tr key={supplier.id} style={{ opacity: supplier.active ? 1 : 0.55, cursor: "pointer" }} onClick={() => onOpen(supplier.id)}>
						<td style={ui.td}><div style={{ fontWeight: 600 }}>{supplier.name}</div>{supplier.taxId ? <div style={ui.tiny}>{supplier.taxId}</div> : null}</td>
						<td style={ui.td}>{supplier.countryCode}</td>
						<td style={ui.td}>{currencyCode(supplier.currencyId)}</td>
						<td style={ui.td}>{supplier.contactName || "-"}<div style={ui.tiny}>{[supplier.phone, supplier.email].filter(Boolean).join(" · ")}</div></td>
						<td style={ui.td}>{supplier.paymentTerms || "-"}</td>
						<td style={{ ...ui.td, ...ui.right }}>{supplier.leadTimeDays !== null ? supplier.leadTimeDays + " días" : "-"}</td>
						<td style={{ ...ui.td, ...ui.right }}>{supplier.orders}</td>
						<td style={ui.td}>{canEdit ? <button type="button" className={ui.btn} onClick={(event) => { event.stopPropagation(); onEdit(supplier); }}>Editar</button> : null}</td>
					</tr>)}</tbody>
				</table>}
		</div>
	</>;
};

const SupplierForm: React.FC<{ campusId: number; catalogs: PurchaseCatalogs; supplier: Supplier | null; onSaved: (saved: Supplier | null) => void; onClose: () => void }> = ({ campusId, catalogs, supplier, onSaved, onClose }) => {
	const [data, setData] = useState<Record<string, any>>({
		name: supplier?.name || "", countryCode: supplier?.countryCode || catalogs.countryCode, currencyId: supplier?.currencyId || catalogs.localCurrencyId,
		contactName: supplier?.contactName || "", phone: supplier?.phone || "", email: supplier?.email || "", taxId: supplier?.taxId || "",
		paymentTerms: supplier?.paymentTerms || "", leadTimeDays: supplier?.leadTimeDays ?? "", notes: supplier?.notes || "", active: supplier ? supplier.active : true
	});
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	// maxLength = size of the tb_suppliers column (longer text used to end in a database error).
	const field = (key: string, caption: string, width: string, type = "text", maxLength?: number) => <div style={ui.field(width)}>
		<span style={ui.caption}>{caption}</span>
		<input type={type} className="form-control" style={ui.input} value={data[key]} maxLength={maxLength} disabled={busy} onChange={(event) => setData({ ...data, [key]: event.target.value })} />
	</div>;
	const save = async () => {
		setBusy(true);
		setError(null);
		try {
			const response = await HttpManager.getInstance().purchaseService.saveSupplier(campusId, supplier ? supplier.id : null, { ...data, active: data.active ? 1 : 0 });
			onSaved(response.data?.supplier || null);
		} catch (requestError: any) {
			setError(errorOf(requestError, "No se pudo guardar el proveedor."));
		} finally {
			setBusy(false);
		}
	};
	return <Modal width="620px" busy={busy} onClose={onClose}>
		<h4 style={{ marginBottom: "12px" }}>{supplier ? supplier.name : "Nuevo proveedor"}</h4>
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
			{field("name", "Nombre *", "100%", "text", 150)}
			<div style={ui.field("150px")}><span style={ui.caption}>País</span>
				<select className="form-select" style={ui.input} value={data.countryCode} disabled={busy} onChange={(event) => setData({ ...data, countryCode: event.target.value })}>
					{catalogs.countries.map((country) => <option key={country.id} value={country.id}>{country.name}</option>)}
				</select></div>
			<div style={ui.field("110px")}><span style={ui.caption}>Moneda</span>
				<select className="form-select" style={ui.input} value={data.currencyId} disabled={busy} onChange={(event) => setData({ ...data, currencyId: Number(event.target.value) })}>
					{catalogs.currencies.map((currency) => <option key={currency.id} value={currency.id}>{currency.code}</option>)}
				</select></div>
			{field("leadTimeDays", "Entrega típica (días)", "150px", "number")}
			{field("taxId", "RFC / RTN", "150px", "text", 40)}
			{field("contactName", "Contacto", "48%", "text", 120)}
			{field("phone", "Teléfono", "48%", "tel", 40)}
			{field("email", "Correo", "48%", "email", 150)}
			{field("paymentTerms", "Condiciones de pago", "48%", "text", 120)}
			<div style={ui.field("100%")}><span style={ui.caption}>Notas</span>
				<textarea className="form-control" rows={2} value={data.notes} disabled={busy} onChange={(event) => setData({ ...data, notes: event.target.value })} style={{ fontSize: "13px" }} /></div>
			{supplier ? <div className="form-check" style={{ marginTop: "4px" }}>
				<input className="form-check-input" type="checkbox" id="supplier-active" checked={!!data.active} disabled={busy} onChange={(event) => setData({ ...data, active: event.target.checked })} />
				<span style={{ fontSize: "13px" }}>Activo</span>
			</div> : null}
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onClose}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || String(data.name).trim().length < 2} onClick={save}>{busy ? "Guardando…" : "Guardar"}</button>
		</div>
	</Modal>;
};

export default Compras;
